import axios, { type AxiosInstance } from 'axios'
import i18n from '@/lib/i18n'

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Helper to get token from zustand persist storage
export function getAuthToken(): string | null {
  // First try direct localStorage (legacy)
  const directToken = localStorage.getItem('token')
  if (directToken) return directToken

  // Then try zustand persist storage
  try {
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      const parsed = JSON.parse(authStorage)
      return parsed?.state?.token || null
    }
  } catch {
    // Ignore parse errors
  }
  return null
}

// Helper to get holding ID from zustand persist storage
export function getHoldingId(): string | null {
  // First try direct localStorage (legacy)
  const directId = localStorage.getItem('holdingId')
  if (directId) return directId

  // Then try zustand persist storage
  try {
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      const parsed = JSON.parse(authStorage)
      return parsed?.state?.activeHoldingId || null
    }
  } catch {
    // Ignore parse errors
  }
  return null
}

// P0 seguridad (2026-06): el gateway ahora rechaza con 401 toda ruta
// protegida sin Bearer. Los fetch crudos (fuera de axios) deben usar este
// wrapper, que agrega Authorization y X-Holding-ID si faltan. No pisa
// headers ya provistos por el caller.
export function authFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers || {})
  const token = getAuthToken()
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  const holdingId = getHoldingId()
  if (holdingId && !headers.has('X-Holding-ID')) {
    headers.set('X-Holding-ID', holdingId)
  }
  const empresaId = localStorage.getItem('empresaId')
  if (empresaId && !headers.has('X-Empresa-ID')) {
    headers.set('X-Empresa-ID', empresaId)
  }
  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', i18n.language || 'es')
  }
  return fetch(input, { ...init, headers })
}

// =============================================================================
// AUTENTICACIÓN ÚNICA DEL FRONTEND
// Regla: toda instancia axios que hable con el gateway se registra con
// applyAuthInterceptors(); todo fetch crudo usa authFetch(). Prohibido
// duplicar interceptores o leer localStorage['token'] a mano (INC-043).
// Contexto: el gateway rechaza con 401 cualquier ruta protegida sin Bearer;
// instancias con interceptor propio sin Authorization (erpApi/pmApi) dejaron
// Doc Studio sin poder extraer catálogos de SAP.
// =============================================================================

// AUTH_06b (#317): pedidos que todavia no tienen sesion. Su 401 es un codigo del
// 2FA malo o un pase vencido, no una sesion expirada: lo muestra la pantalla.
// AUTH_06f (#321): + los dos pasos del alta obligatoria, que usan el pase de alta.
const NO_SESSION_401_PATHS = new Set(['/auth/mfa/login', '/auth/mfa/enroll/setup', '/auth/mfa/enroll/confirm'])

// AUTH_06c (#318): en Ajustes la sesion esta viva. Un 401 de estos pedidos con uno de
// estos codigos es la contrasena o el codigo del 2FA mal escritos, y lo muestra la
// ventana; cualquier otro 401 (sin codigo) sigue siendo una sesion vencida.
const MFA_SETTINGS_PATHS = new Set([
  '/auth/mfa/setup',
  '/auth/mfa/verify',
  '/auth/mfa/disable',
  '/auth/mfa/backup-codes',
])
const MFA_SETTINGS_401_CODES = new Set(['mfa_password_invalid', 'mfa_code_invalid'])

type UnauthorizedError = {
  response?: { status?: number; data?: unknown }
  config?: { url?: string }
}

function isExpiredSession(error: UnauthorizedError): boolean {
  if (error.response?.status !== 401) return false
  const url = error.config?.url ?? ''
  if (NO_SESSION_401_PATHS.has(url)) return false
  const code = (error.response.data as { detail?: { code?: unknown } } | undefined)?.detail?.code
  return !(MFA_SETTINGS_PATHS.has(url) && typeof code === 'string' && MFA_SETTINGS_401_CODES.has(code))
}

// Response interceptor - handle errors with friendly UX
function handleUnauthorized(error: UnauthorizedError) {
  if (isExpiredSession(error)) {
    // Token expired or invalid - show friendly message
    // Import toast dynamically to avoid circular dependencies
    import('@/components/ui/use-toast').then(({ toast }) => {
      toast({
        variant: 'destructive',
        title: 'Sesión expirada',
        description: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
      })
    })

    // Clear all auth data
    localStorage.removeItem('token')
    localStorage.removeItem('holdingId')
    localStorage.removeItem('empresaId')
    localStorage.removeItem('auth-storage') // Zustand persist

    // Redirect after brief delay so user sees the toast
    setTimeout(() => {
      window.location.href = '/login'
    }, 1500)
  }
  return Promise.reject(error)
}

export function applyAuthInterceptors(instance: AxiosInstance): AxiosInstance {
  instance.interceptors.request.use((config) => {
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add tenant headers
    const holdingId = getHoldingId()
    const empresaId = localStorage.getItem('empresaId')
    if (holdingId) {
      config.headers['X-Holding-ID'] = holdingId
    }
    if (empresaId) {
      config.headers['X-Empresa-ID'] = empresaId
    }

    // Add language header for i18n-aware endpoints
    config.headers['Accept-Language'] = i18n.language || 'es'

    return config
  })

  instance.interceptors.response.use((response) => response, handleUnauthorized)

  return instance
}

applyAuthInterceptors(api)

// ALTA-014: Tenant Bootstrap Response Interface
export interface TenantBootstrapResponse {
  tenantSlug: string | null
  holdingId: string | null
  holdingName: string | null
  branding: {
    logoUrl: string | null
    primaryColor: string
    secondaryColor: string
    faviconUrl: string | null
  }
  authPolicy: {
    allowPasswordLogin: boolean
    mfaRequired: boolean
    defaultMethod: string
    availableSSOProviders: Array<{
      providerType: string
      displayName: string
      icon: string | null
    }>
  }
  error: string | null
}

// AUTH_06c (#318): estado del 2FA para Ajustes
export interface MFAStatus {
  mfa_enabled: boolean
  backup_codes_remaining: number
  // por que no se puede administrar desde Ajustes: 'mfa_password_not_set' | 'mfa_policy_missing'
  unavailable_reason: string | null
}

// API endpoints
export const authApi = {
  // ALTA-014: Public tenant bootstrap endpoint (no auth required)
  tenantBootstrap: (overrideHost?: string) =>
    api.get<TenantBootstrapResponse>('/public/tenant/bootstrap', {
      params: overrideHost ? { host: overrideHost } : undefined,
    }),
  // AUTH_04 (#298): en la direccion comun, el dominio del correo indica el holding.
  // El correo va en el cuerpo, nunca en la direccion.
  tenantDiscover: (email: string) =>
    api.post<TenantBootstrapResponse>('/public/tenant/discover', { email }),

  login: (email: string, password: string, holdingId?: string) =>
    api.post('/auth/login', { email, password, holding_id: holdingId }),
  register: (data: { email: string; password: string; full_name: string }) => {
    const nameParts = data.full_name.trim().split(' ')
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || nameParts[0] || ''
    return api.post('/auth/register', {
      email: data.email,
      password: data.password,
      first_name: firstName,
      last_name: lastName,
    })
  },
  // AUTH_06b (#317): segundo paso del ingreso con 2FA (pase de /auth/login + codigo)
  mfaLogin: (mfaToken: string, code: string) =>
    api.post('/auth/mfa/login', { mfa_token: mfaToken, code }),
  // AUTH_06f (#321): alta obligatoria tras la contrasena, con el pase de alta (sin sesion).
  // Confirmar devuelve la respuesta del ingreso mas los codigos de respaldo, una sola vez.
  mfaEnrollSetup: (enrollmentToken: string) =>
    api.post<{ secret: string; qr_code_uri: string; expires_in: number }>('/auth/mfa/enroll/setup', {
      enrollment_token: enrollmentToken,
    }),
  mfaEnrollConfirm: (enrollmentToken: string, code: string) =>
    api.post('/auth/mfa/enroll/confirm', { enrollment_token: enrollmentToken, code }),
  // AUTH_06c (#318): el 2FA desde Ajustes. Los codigos de respaldo llegan una sola vez.
  mfaStatus: () => api.get<MFAStatus>('/auth/mfa/status'),
  mfaSetup: (password: string) =>
    api.post<{ secret: string; qr_code_uri: string }>('/auth/mfa/setup', { password }),
  mfaVerify: (code: string) =>
    api.post<{ backup_codes: string[] }>('/auth/mfa/verify', { code }),
  mfaDisable: (password: string, code: string) =>
    api.post('/auth/mfa/disable', { password, code }),
  mfaNewBackupCodes: (password: string, code: string) =>
    api.post<{ backup_codes: string[] }>('/auth/mfa/backup-codes', { password, code }),
  forgotPassword: (email: string) =>
    api.post('/auth/password-reset', { email }),
  // AUTH_20 (#331): el enlace del correo trae el token; vale 1 hora y sirve una vez
  confirmPasswordReset: (token: string, newPassword: string) =>
    api.post('/auth/password-reset/confirm', { token, new_password: newPassword }),
  me: () => api.get('/auth/me'),
  // RBAC: Get user permissions, roles, and feature flags
  // INC-061: Correct endpoint is /v1/me/permissions (me router), not /auth/me/permissions
  myPermissions: () => api.get<{
    permissions: string[]
    roles: Array<{ code: string; name: string; scope_type: string }>
    features: string[]
  }>('/v1/me/permissions'),
  // INC-077: Get products available for current holding
  myProducts: () => api.get<{
    total_available: number
    total_enabled: number
    products: Array<{
      id: string | null
      product_code: string
      enabled: boolean
      is_valid: boolean
      name: string
      description: string
      modules: string[]
    }>
    enabled_codes: string[]
  }>('/v1/products/me/products'),
  // INC-031: Multi-holding support
  selectHolding: (holdingId: string, tempToken: string) =>
    api.post('/auth/select-holding', { holding_id: holdingId, temp_token: tempToken }),
  switchHolding: (holdingId: string) =>
    api.post('/auth/switch-holding', { holding_id: holdingId }),
  // SSO Enterprise
  getSSOProviders: (holdingId?: string) =>
    api.get<{
      providers: Array<{
        id: string
        name: string
        display_name: string
        icon: string
        color: string
        enabled: boolean
      }>
      traditional_login_enabled: boolean
      default_method: string
    }>('/auth/sso/providers', { params: holdingId ? { holding_id: holdingId } : undefined }),
  initiateSSOLogin: (providerId: string, holdingId?: string) =>
    api.get<{
      auth_url: string
      state: string
    }>(`/auth/sso/${providerId}/authorize`, {
      params: {
        ...(holdingId ? { holding_id: holdingId } : {}),
        // ALTA-014: Send current hostname for redirect after SSO callback
        return_host: window.location.host,
      }
    }),
  handleSSOCallback: (providerId: string, code: string, state: string) =>
    api.post(`/auth/sso/${providerId}/callback`, { code, state }),
  // AUTH_18 (#323): la cookie que abre la documentacion de la API (/docs). Solo con el
  // permiso platform.api_docs.read; `withCredentials` para que el navegador la guarde.
  apiDocsSession: () => api.post('/auth/api-docs/session', null, { withCredentials: true }),
  // AUTH_18 (#323): el servidor borra esa cookie (la pagina no puede: es HttpOnly)
  logout: () => api.post('/auth/logout', null, { withCredentials: true }),
}

// AUTH_18 (#323): la documentacion de la API, en el gateway
export const API_DOCS_URL = `${API_BASE_URL.replace(/\/+$/, '')}/docs`

export const processMiningApi = {
  analyze: (eventLogId: string, options: object) =>
    api.post('/v1/pm/mining/analyze', { event_log_id: eventLogId, ...options }),
  uploadAndAnalyze: (file: File, options: object) => {
    const formData = new FormData()
    formData.append('file', file)
    Object.entries(options).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => formData.append(key, String(v)))
      } else {
        formData.append(key, String(value))
      }
    })
    return api.post('/v1/pm/mining/analyze/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  getVariants: (file: File, maxVariants = 10) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('max_variants', String(maxVariants))
    return api.post('/v1/pm/mining/variants', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  // Discovery API - Get pre-computed DFG, variants, bottlenecks from extraction
  // lang: localiza los nombres de actividad al idioma de la UI (opción A).
  getDiscovery: (configId: string, lang?: string) =>
    api.get(`/v1/pm/pm/discovery/${configId}${lang ? `?lang=${lang.split('-')[0]}` : ''}`),
  runDiscovery: (configId: string) =>
    api.post(`/v1/pm/pm/discovery/${configId}/run`),
  getDfg: (configId: string) =>
    api.get(`/v1/pm/pm/discovery/${configId}/dfg`),
  getBottlenecks: (configId: string) =>
    api.get(`/v1/pm/pm/discovery/${configId}/bottlenecks`),
  // AI Analysis API - Generate intelligent process reports
  // reportType options: general, bottlenecks, variants, performance, recommendations, transformation, conformance, sod, rca
  generateAnalysisReport: (
    configId: string,
    language: string = 'es',
    includeRecommendations: boolean = true,
    includeBottleneckAnalysis: boolean = true,
    depthLevel: number = 2, // 1=breve (~500 words), 2=estándar (~1200 words), 3=profundo (~2000+ words)
    anonymize: boolean = false, // Anonimiza datos sensibles (DOC-001, USR-001, etc.)
    reportType: string = 'general' // Tipo de reporte especializado
  ) =>
    api.post(`/v1/pm/pm/analysis/generate-report`, {
      config_id: configId,
      language,
      include_recommendations: includeRecommendations,
      include_bottleneck_analysis: includeBottleneckAnalysis,
      depth_level: depthLevel,
      save_to_library: true, // Always save PM reports to docs-library
      anonymize,
      report_type: reportType,
    }),

  // Export Report to DOCX/PDF (NEVER markdown to user per PM_REPORTS spec)
  exportReport: (
    configId: string,
    format: 'docx' | 'pdf',
    language: string = 'es',
    includeRecommendations: boolean = true
  ) =>
    api.post(
      `/v1/pm/pm/analysis/export-report`,
      {
        config_id: configId,
        format,
        language,
        include_recommendations: includeRecommendations,
      },
      {
        responseType: 'blob', // Important: receive as binary
      }
    ),
}

export const masterDataApi = {
  getCompanies: (anonymize = false) =>
    api.get(`/v1/pm/pm/master-data/companies`, { params: { anonymize } }),
  getPlants: (anonymize = false) =>
    api.get(`/v1/pm/pm/master-data/plants`, { params: { anonymize } }),
  getMaterials: (anonymize = false, search?: string, limit = 500, offset = 0) =>
    api.get(`/v1/pm/pm/master-data/materials`, { params: { anonymize, search, limit, offset } }),
  getMaterialGroups: (anonymize = false) =>
    api.get(`/v1/pm/pm/master-data/material-groups`, { params: { anonymize } }),
  getVendors: (anonymize = false) =>
    api.get(`/v1/pm/pm/master-data/vendors`, { params: { anonymize } }),
  getCustomers: (anonymize = false) =>
    api.get(`/v1/pm/pm/master-data/customers`, { params: { anonymize } }),
  getCostCenters: (anonymize = false) =>
    api.get(`/v1/pm/pm/master-data/cost-centers`, { params: { anonymize } }),
  getSummary: () =>
    api.get(`/v1/pm/pm/master-data/summary`),
}

export const kpiApi = {
  getAvailable: () => api.get('/v1/pm/kpis/available'),
  calculate: (eventLogId: string, kpis?: string[]) =>
    api.post('/v1/pm/kpis/calculate', { event_log_id: eventLogId, kpis }),
  uploadAndCalculate: (file: File, kpis?: string[]) => {
    const formData = new FormData()
    formData.append('file', file)
    if (kpis) {
      kpis.forEach((kpi) => formData.append('kpis', kpi))
    }
    return api.post('/v1/pm/kpis/calculate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  getRework: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/v1/pm/kpis/rework', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

export const rulesApi = {
  list: () => api.get('/v1/pm/rules/'),
  create: (name: string, description: string, rules: object[]) =>
    api.post('/v1/pm/rules/sets', rules, {
      params: { name, description },
    }),
  get: (setId: string) => api.get(`/v1/pm/rules/sets/${setId}`),
  delete: (setId: string) => api.delete(`/v1/pm/rules/sets/${setId}`),
  evaluate: (eventLogId: string, ruleSetId: string) =>
    api.post('/v1/pm/rules/evaluate', null, {
      params: { event_log_id: eventLogId, rule_set_id: ruleSetId },
    }),
  generate: (file: File, confidenceLevel = 0.95) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/v1/pm/rules/generate', formData, {
      params: { confidence_level: confidenceLevel },
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

// Chat API Types (LangGraph Service)
export interface ChatMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export interface ChatResponse {
  conversation_id: string
  message: ChatMessage
  metadata?: Record<string, any>
}

export interface ChatRequest {
  message: string
  conversation_id?: string
  user_id: string
  tenant_id: string
  // LLM_11 (#496): el navegador manda el NOMBRE de un prompt de plataforma permitido,
  // nunca el texto de las instrucciones; lang_graph lo resuelve en Prompt Service.
  prompt_name?: string
  prompt_variables?: Record<string, string | number>
}

// Chat API
export const chatApi = {
  // Send message to AI assistant (standard chat)
  send: (params: {
    message: string
    conversationId?: string
    userId: string
    tenantId: string
    promptName?: string
    promptVariables?: Record<string, string | number>
  }): Promise<{ data: ChatResponse }> =>
    api.post('/v1/chat/', {
      message: params.message,
      conversation_id: params.conversationId,
      user_id: params.userId,
      tenant_id: params.tenantId,
      prompt_name: params.promptName,
      prompt_variables: params.promptVariables,
    }),
}

// Canvas API - Sprint 5: Workspace/Canvas MVP
export interface Canvas {
  id: string
  name: string
  canvas_type: 'document' | 'code' | 'table' | 'diagram' | 'plan' | 'spec'
  content: string
  language?: string | null
  description?: string | null
  tags: string[]
  metadata: Record<string, unknown>
  user_id: string
  tenant_id: string
  status: 'draft' | 'validated' | 'archived' | 'obsolete'
  current_version: number
  conversation_id?: string | null
  created_at: string
  updated_at: string
}

export interface CanvasVersion {
  version: number
  content: string
  diff_from_previous?: string | null
  created_at: string
  created_by: string
  comment?: string | null
}

export interface CanvasWithHistory extends Canvas {
  versions: CanvasVersion[]
}

export interface CanvasListItem {
  id: string
  name: string
  canvas_type: string
  status: string
  current_version: number
  description?: string | null
  tags: string[]
  conversation_id?: string | null
  created_at: string
  updated_at: string
  content_preview: string
}

export interface CanvasListResponse {
  total: number
  items: CanvasListItem[]
}

export interface CreateCanvasDTO {
  name: string
  canvas_type: 'document' | 'code' | 'table' | 'diagram' | 'plan' | 'spec'
  content?: string
  language?: string
  description?: string
  tags?: string[]
  metadata?: Record<string, unknown>
  conversation_id?: string
}

export interface UpdateCanvasDTO {
  name?: string
  content?: string
  description?: string
  status?: 'draft' | 'validated' | 'archived' | 'obsolete'
  tags?: string[]
  metadata?: Record<string, unknown>
  comment?: string
}

// Canvas API uses lang-graph service through gateway.
// RBAC_56 (#511): el gateway solo publica las rutas que estan aqui; el resto da 404.
export const canvasApi = {
  // List user's canvases
  list: (options?: {
    canvas_type?: string
    status?: string
    tags?: string
    search?: string
    limit?: number
    offset?: number
  }) =>
    api.get<CanvasListResponse>('/v1/chat/canvas', { params: options }),

  // Get canvas with history
  getWithHistory: (id: string) =>
    api.get<CanvasWithHistory>(`/v1/chat/canvas/${id}/history`),

  // Create canvas
  create: (data: CreateCanvasDTO) =>
    api.post<Canvas>('/v1/chat/canvas', data),

  // Update canvas
  update: (id: string, data: UpdateCanvasDTO) =>
    api.patch<Canvas>(`/v1/chat/canvas/${id}`, data),

  // Validate canvas
  validate: (id: string) =>
    api.post<Canvas>(`/v1/chat/canvas/${id}/validate`),

  // Delete canvas (soft by default)
  delete: (id: string, hardDelete = false) =>
    api.delete(`/v1/chat/canvas/${id}`, { params: { hard_delete: hardDelete } }),
}

// Admin API - Gestión de jerarquía organizacional
import type {
  ChannelPartner,
  CreateChannelPartnerDTO,
  UpdateChannelPartnerDTO,
  Holding,
  Empresa,
  Area,
  AdminUser,
  ERPConnection,
  UserAssignment,
  CreateHoldingDTO,
  UpdateHoldingDTO,
  CreateEmpresaDTO,
  UpdateEmpresaDTO,
  CreateAreaDTO,
  UpdateAreaDTO,
  CreateUserDTO,
  UpdateUserDTO,
  AddERPConnectionDTO,
  ListOptions,
  PaginatedResponse,
  ConnectionTestResult,
  MFAPolicyLimits,
  SystemLLMStatus,
  HoldingRetention,
  RetentionCertificate,
  RetentionClientsResponse,
} from '@/types/admin'

import type {
  ERPConnectionFull,
  CreateERPConnectionDTO,
  UpdateERPConnectionDTO,
  TestConnectionRequest,
  TestConnectionResult,
} from '@/types/erp'

import type {
  DataSource,
  DataSourceDetail,
  DataSourceListResponse,
  DataSourceExportResponse,
  CreateDataSourceDTO,
  UpdateDataSourceDTO,
  DataSourceEntity,
  DataSourceEntityWithBuilders,
  CreateEntityDTO,
  UpdateEntityDTO,
  DataSourceBuilder,
  CreateBuilderDTO,
  UpdateBuilderDTO,
  DataSourceOperation,
  CreateOperationDTO,
  UpdateOperationDTO,
  DiscoveryResponse,
  ExecuteRequest,
  ExecuteResponse,
  TestQueryRequest,
  TestQueryResponse,
  FetchExampleResponse,
} from '@/types/datasource'

import type {
  PlatformPrompt,
  PlatformPromptListResponse,
  PlatformPromptVersionListResponse,
  CreatePlatformPromptDTO,
  UpdatePlatformPromptDTO,
  TenantPrompt,
  TenantPromptListResponse,
  CreateTenantPromptDTO,
  UpdateTenantPromptDTO,
} from '@/types/prompts'

// Channel Partners API - Nivel 1 (distribuidores)
export const channelPartnersApi = {
  list: (options?: ListOptions) =>
    api.get<PaginatedResponse<ChannelPartner>>('/v1/admin/channel-partners', { params: options }),
  get: (id: string) =>
    api.get<ChannelPartner>(`/v1/admin/channel-partners/${id}`),
  create: (data: CreateChannelPartnerDTO) =>
    api.post<ChannelPartner>('/v1/admin/channel-partners', data),
  update: (id: string, data: UpdateChannelPartnerDTO) =>
    api.patch<ChannelPartner>(`/v1/admin/channel-partners/${id}`, data),
  delete: (id: string) =>
    api.delete(`/v1/admin/channel-partners/${id}`),
}

export const holdingsApi = {
  list: (options?: ListOptions) =>
    api.get<PaginatedResponse<Holding>>('/v1/admin/holdings', { params: options }),
  get: (id: string) =>
    api.get<Holding>(`/v1/admin/holdings/${id}`),
  // Master data del holding del usuario actual (nombre + industria).
  // Accesible a cualquier usuario del holding (no requiere rol admin) — lo usa
  // el wizard de Relevamiento para auto-rellenar Cliente e Industria.
  currentMasterData: () =>
    api.get<{ id: string; name: string; industry: string | null }>(
      '/v1/admin/holdings/current/master-data'
    ),
  create: (data: CreateHoldingDTO) =>
    api.post<Holding>('/v1/admin/holdings', data),
  update: (id: string, data: UpdateHoldingDTO) =>
    api.patch<Holding>(`/v1/admin/holdings/${id}`, data),
  // AUTH_06a1 (#334): límites de la regla del 2FA; la pantalla no repite los números
  getMfaPolicyLimits: () =>
    api.get<{ limits: MFAPolicyLimits }>('/v1/admin/holdings/mfa-policy/limits'),
  // F5_21a (#613) / F5_21b (#614): plazos de borrado; solo el GAM Admin y solo lectura
  getRetention: (id: string) =>
    api.get<HoldingRetention>(`/v1/admin/holdings/${id}/retention`),
  // Si la plataforma tiene su proveedor de IA configurado (para "usar el del sistema")
  getSystemLLMStatus: () =>
    api.get<SystemLLMStatus>('/v1/admin/holdings/llm/system-status'),
  delete: (id: string) =>
    api.delete(`/v1/admin/holdings/${id}`),
  testLLM: (id: string) =>
    api.post<ConnectionTestResult>(`/v1/admin/holdings/${id}/test-llm`),
  testAuth: (id: string) =>
    api.post<ConnectionTestResult>(`/v1/admin/holdings/${id}/test-auth`),
  getCascadeDeletePreview: (id: string) =>
    api.get(`/v1/admin/holdings/${id}/cascade-delete/preview`),
  executeCascadeDelete: (id: string, confirmation: string) =>
    api.post(`/v1/admin/holdings/${id}/cascade-delete/execute`, { confirmation }),
  // INC-077: License summary for Mandin/CP dashboard
  getLicenseSummary: () =>
    api.get<{
      total_license_count: number
      total_active_users: number
      total_available: number
      overall_utilization_percent: number
      holdings_count: number
      holdings_exceeded: number
      holdings: Array<{
        holding_id: string
        holding_name: string
        holding_code: string
        license_count: number
        license_type: string
        active_users: number
        licenses_available: number
        is_exceeded: boolean
        utilization_percent: number
      }>
      channel_partner_id: string | null
      channel_partner_name: string | null
    }>('/v1/admin/holdings/licenses/summary'),
}

export const empresasApi = {
  list: (holdingId?: string, options?: ListOptions) =>
    api.get<PaginatedResponse<Empresa>>('/v1/admin/empresas', {
      params: { holding_id: holdingId, ...options },
    }),
  get: (id: string) =>
    api.get<Empresa>(`/v1/admin/empresas/${id}`),
  create: (data: CreateEmpresaDTO) =>
    api.post<Empresa>('/v1/admin/empresas', data),
  update: (id: string, data: UpdateEmpresaDTO) =>
    api.patch<Empresa>(`/v1/admin/empresas/${id}`, data),
  delete: (id: string) =>
    api.delete(`/v1/admin/empresas/${id}`),

  // ERP Connections
  listConnections: (empresaId: string) =>
    api.get<ERPConnection[]>(`/v1/admin/empresas/${empresaId}/connections`),
  addConnection: (data: AddERPConnectionDTO) =>
    api.post<ERPConnection>(`/v1/admin/empresas/${data.empresa_id}/connections`, data),
  updateConnection: (empresaId: string, connectionId: string, data: Partial<AddERPConnectionDTO>) =>
    api.patch<ERPConnection>(`/v1/admin/empresas/${empresaId}/connections/${connectionId}`, data),
  deleteConnection: (empresaId: string, connectionId: string) =>
    api.delete(`/v1/admin/empresas/${empresaId}/connections/${connectionId}`),
  testConnection: (empresaId: string, connectionId: string) =>
    api.post<ConnectionTestResult>(`/v1/admin/empresas/${empresaId}/connections/${connectionId}/test`),
}

export const areasApi = {
  list: (empresaId?: string, options?: ListOptions) =>
    api.get<PaginatedResponse<Area>>('/v1/admin/areas', {
      params: { empresa_id: empresaId, ...options },
    }),
  get: (id: string) =>
    api.get<Area>(`/v1/admin/areas/${id}`),
  create: (data: CreateAreaDTO) =>
    api.post<Area>('/v1/admin/areas', data),
  update: (id: string, data: UpdateAreaDTO) =>
    api.patch<Area>(`/v1/admin/areas/${id}`, data),
  delete: (id: string) =>
    api.delete(`/v1/admin/areas/${id}`),
}

export const usersApi = {
  list: (options?: ListOptions & { empresa_id?: string; area_id?: string; is_emergency_account?: boolean }) =>
    api.get<PaginatedResponse<AdminUser>>('/v1/admin/users', { params: options }),
  get: (id: string) =>
    api.get<AdminUser>(`/v1/admin/users/${id}`),
  create: (data: CreateUserDTO) =>
    api.post<AdminUser>('/v1/admin/users', data),
  update: (id: string, data: UpdateUserDTO) =>
    api.patch<AdminUser>(`/v1/admin/users/${id}`, data),
  // RBAC_32 (#348): una baja desactiva la cuenta (no la borra) y cierra sus sesiones
  deactivate: (id: string) =>
    api.post<AdminUser>(`/v1/admin/users/${id}/deactivate`),
  reactivate: (id: string) =>
    api.post<AdminUser>(`/v1/admin/users/${id}/reactivate`),
  resetPassword: (id: string) =>
    api.post(`/v1/admin/users/${id}/reset-password`),
  // AUTH_06d (#319): quita el 2FA de otra persona (permiso auth.mfa.admin)
  resetMfa: (id: string) =>
    api.post<AdminUser>(`/v1/admin/users/${id}/mfa/reset`),
  // AUTH_14 (#312): marca o desmarca una cuenta de emergencia (solo GAM Admin)
  setEmergencyAccount: (id: string, isEmergencyAccount: boolean) =>
    api.put<AdminUser>(`/v1/admin/users/${id}/emergency-account`, { is_emergency_account: isEmergencyAccount }),
  // F5_21b (#614): marca o desmarca a un GAM Admin para los avisos de borrado (solo GAM Admin)
  setRetentionAlerts: (id: string, retentionAlerts: boolean) =>
    api.put<AdminUser>(`/v1/admin/users/${id}/retention-alerts`, { retention_alerts: retentionAlerts }),

  // Assignments
  addAssignment: (userId: string, assignment: Omit<UserAssignment, 'id' | 'user_id' | 'empresa_name' | 'area_name'>) =>
    api.post<UserAssignment>(`/v1/admin/users/${userId}/assignments`, assignment),
  updateAssignment: (userId: string, assignmentId: string, data: Partial<UserAssignment>) =>
    api.patch<UserAssignment>(`/v1/admin/users/${userId}/assignments/${assignmentId}`, data),
  removeAssignment: (userId: string, assignmentId: string) =>
    api.delete(`/v1/admin/users/${userId}/assignments/${assignmentId}`),

  // INC-031: User Holdings CRUD
  listHoldings: (userId: string) =>
    api.get<{ holdings: Array<{ id: string; holding_id: string; holding_code: string; holding_name: string; is_default: boolean }>; default_holding_id: string | null; total: number }>(`/v1/admin/users/${userId}/holdings`),
  addHolding: (userId: string, holdingId: string, isDefault?: boolean) =>
    api.post(`/v1/admin/users/${userId}/holdings`, { holding_id: holdingId, is_default: isDefault }),
  bulkAssignHoldings: (userId: string, holdings: Array<{ holding_id: string; is_default: boolean }>) => {
    const holding_ids = holdings.map(h => h.holding_id)
    const defaultHolding = holdings.find(h => h.is_default)
    return api.post(`/v1/admin/users/${userId}/holdings/bulk`, {
      holding_ids,
      default_holding_id: defaultHolding?.holding_id || null
    })
  },
  setDefaultHolding: (userId: string, holdingId: string) =>
    api.patch(`/v1/admin/users/${userId}/holdings/${holdingId}/default`),
  removeHolding: (userId: string, holdingId: string) =>
    api.delete(`/v1/admin/users/${userId}/holdings/${holdingId}`),
}

// Helper to transform frontend ERP data to backend inline format
function transformToBackendInline(data: CreateERPConnectionDTO) {
  const holdingId = localStorage.getItem('holdingId')

  // Map frontend erp_system to backend erp_type
  const erpTypeMap: Record<string, string> = {
    'sap_business_one': 'sap_b1',
    'sap_s4hana_cloud': 'sap_s4hana_cloud',
    'sap_s4hana_private': 'sap_s4hana_op',
    'sap_r3_ecc': 'sap_r3_ecc',  // R/3 / ECC uses RFC only
    'dynamics_365_fo': 'dynamics_365',
    'dynamics_365_bc': 'dynamics_bc',
    'dynamics_365_ce': 'dynamics_365',
    'oracle_ebs': 'oracle_erp',
    'oracle_fusion': 'oracle_erp',
  }

  // Map frontend protocol to backend protocol
  const protocolMap: Record<string, string> = {
    'odata': 'odata_v4',
    'service_layer': 'rest',
    'rfc': 'rfc',
    'soap': 'soap',
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const backendData: Record<string, any> = {
    name: data.name,
    description: data.description,
    erp_type: erpTypeMap[data.erp_system] || data.erp_system,
    protocol: protocolMap[data.protocol] || 'odata_v4',
    holding_id: holdingId,
    empresa_id: data.empresa_id,
    environment: data.environment,
    is_primary: data.is_primary || false,
    pm_config_scope: data.pm_config_scope || 'global',
    rfc_custom_read: data.rfc_custom_read || false,
    tags: [],
  }

  // Extract credentials based on protocol
  if (data.protocol === 'service_layer' && data.service_layer_config) {
    // SAP Business One Service Layer
    backendData.base_url = data.service_layer_config.base_url
    backendData.sap_b1_company_db = data.service_layer_config.company_db
    backendData.verify_ssl = data.service_layer_config.verify_ssl ?? true
    backendData.username = data.service_layer_config.username
    backendData.password = data.service_layer_config.password
    backendData.auth_type = 'basic'
  } else if (data.protocol === 'odata' && data.odata_config) {
    // OData (S/4HANA Cloud, Dynamics 365)
    backendData.base_url = data.odata_config.base_url
    backendData.sap_client = data.odata_config.sap_client
    backendData.sap_language = data.odata_config.language || 'EN'
    backendData.username = data.odata_config.username
    backendData.password = data.odata_config.password
    backendData.auth_type = data.odata_config.auth_type || 'basic'
    // OAuth2 fields
    if (data.odata_config.auth_type === 'oauth2') {
      backendData.client_id = data.odata_config.oauth_client_id
      backendData.client_secret = data.odata_config.oauth_client_secret
      backendData.oauth_tenant_id = data.odata_config.oauth_tenant_id
      backendData.oauth_scope = data.odata_config.oauth_scope
      backendData.token_url = `https://login.microsoftonline.com/${data.odata_config.oauth_tenant_id}/oauth2/v2.0/token`
    }
  } else if (data.protocol === 'rfc' && data.rfc_config) {
    // RFC (SAP R/3, ECC, S/4HANA On-Premise)
    backendData.base_url = `http://${data.rfc_config.host}`
    backendData.rfc_host = data.rfc_config.host
    backendData.rfc_sysnr = data.rfc_config.system_number
    backendData.rfc_system_id = data.rfc_config.system_id
    backendData.rfc_group = data.rfc_config.logon_group
    backendData.rfc_use_load_balancing = data.rfc_config.use_load_balancing || false
    backendData.rfc_message_server_host = data.rfc_config.message_server_host
    backendData.rfc_message_server_port = data.rfc_config.message_server_port
    backendData.rfc_sap_router_string = data.rfc_config.sap_router_string
    backendData.sap_client = data.rfc_config.client
    backendData.sap_language = data.rfc_config.language || 'ES'
    backendData.username = data.rfc_config.username
    backendData.password = data.rfc_config.password
    backendData.auth_type = 'basic'
  } else if (data.protocol === 'soap' && data.soap_config) {
    // SOAP (Web Services)
    backendData.base_url = data.soap_config.endpoint_url || data.soap_config.wsdl_url
    backendData.soap_wsdl_url = data.soap_config.wsdl_url
    backendData.soap_endpoint_url = data.soap_config.endpoint_url
    backendData.soap_ws_security = data.soap_config.ws_security || false
    backendData.soap_security_token_type = data.soap_config.security_token_type
    backendData.username = data.soap_config.username
    backendData.password = data.soap_config.password
    backendData.auth_type = 'basic'
  }

  return backendData
}

// Helper to transform backend response to frontend format
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformFromBackend(backendData: any): ERPConnectionFull {
  // Map backend erp_type to frontend erp_system
  const erpSystemMap: Record<string, string> = {
    'sap_b1': 'sap_business_one',
    'sap_s4hana_cloud': 'sap_s4hana_cloud',
    'sap_s4hana_op': 'sap_s4hana_private',
    'sap_r3_ecc': 'sap_r3_ecc',  // R/3 / ECC
    'dynamics_365': 'dynamics_365_fo',
    'dynamics_bc': 'dynamics_365_bc',
    'oracle_erp': 'oracle_fusion',
  }

  // Map backend protocol to frontend protocol
  const protocolMap: Record<string, string> = {
    'odata_v4': 'odata',
    'odata_v2': 'odata',
    'rest': 'service_layer',
    'rfc': 'rfc',
    'soap': 'soap',
  }

  // Map auth_type to frontend format
  const authTypeMap: Record<string, 'basic' | 'oauth2' | 'bearer_token'> = {
    'basic': 'basic',
    'oauth2_client_credentials': 'oauth2',
    'oauth2_password': 'oauth2',
  }

  return {
    id: backendData.id,
    empresa_id: backendData.empresa_id || backendData.holding_id,
    name: backendData.name,
    description: backendData.description,
    erp_system: (erpSystemMap[backendData.erp_type] || backendData.erp_type) as ERPConnectionFull['erp_system'],
    protocol: protocolMap[backendData.protocol] || backendData.protocol,
    environment: backendData.environment,
    status: backendData.status,
    is_primary: backendData.is_primary,
    pm_config_scope: backendData.pm_config_scope || 'global',
    rfc_custom_read: backendData.rfc_custom_read || false,
    // Credentials (username visible, password indicator)
    username: backendData.username,
    has_password: backendData.has_password || false,
    // SAP B1 Service Layer config
    service_layer_config: backendData.sap_b1_company_db ? {
      base_url: backendData.base_url,
      company_db: backendData.sap_b1_company_db,
      verify_ssl: backendData.verify_ssl ?? true,
      username: backendData.username || '',
      password_set: backendData.has_password || false,
    } : undefined,
    // RFC config (SAP R/3, ECC, S/4HANA On-Premise)
    rfc_config: backendData.rfc_host ? {
      host: backendData.rfc_host,
      system_number: backendData.rfc_sysnr || '00',
      system_id: backendData.rfc_system_id,
      client: backendData.sap_client || '100',
      language: backendData.sap_language || 'EN',
      username: backendData.username || '',
      password_set: backendData.has_password || false,
      // Load balancing
      logon_group: backendData.rfc_group,
      use_load_balancing: backendData.rfc_use_load_balancing || false,
      message_server_host: backendData.rfc_message_server_host,
      message_server_port: backendData.rfc_message_server_port,
      sap_router_string: backendData.rfc_sap_router_string,
    } : undefined,
    // OData config (for S/4HANA Cloud, Dynamics 365)
    odata_config: (backendData.protocol === 'odata_v4' || backendData.protocol === 'odata_v2') && !backendData.sap_b1_company_db && !backendData.rfc_host && !backendData.soap_wsdl_url ? {
      base_url: backendData.base_url,
      auth_type: authTypeMap[backendData.auth_type] || 'basic',
      sap_client: backendData.sap_client,
      language: backendData.sap_language,
      username: backendData.username || '',
      password_set: backendData.has_password || false,
      // OAuth2 fields
      oauth_tenant_id: backendData.oauth_tenant_id,
      oauth_client_id: backendData.oauth_client_id,
      oauth_scope: backendData.oauth_scope,
    } : undefined,
    // SOAP config (Web Services)
    soap_config: backendData.soap_wsdl_url || backendData.soap_endpoint_url ? {
      wsdl_url: backendData.soap_wsdl_url,
      endpoint_url: backendData.soap_endpoint_url,
      ws_security: backendData.soap_ws_security || false,
      security_token_type: backendData.soap_security_token_type,
      username: backendData.username || '',
      password_set: backendData.has_password || false,
    } : undefined,
    created_at: backendData.created_at,
    updated_at: backendData.updated_at,
    // Test result fields
    last_test_at: backendData.last_test_at,
    last_test_result: backendData.last_test_result,
    last_test_message: backendData.last_error,
    last_test_response_time_ms: backendData.last_test_response_time_ms,
  }
}

// ERP Connections API - Sprint 2
export const erpConnectionsApi = {
  // List all connections (optionally filtered by empresa)
  list: (_empresaId?: string, _options?: ListOptions) =>
    api.get('/v1/erp/connections').then(response => {
      // Transform backend response array to frontend format
      const connections = Array.isArray(response.data)
        ? response.data.map(transformFromBackend)
        : []
      return { ...response, data: { items: connections, total: connections.length } }
    }),

  // Get single connection
  get: (id: string) =>
    api.get(`/v1/erp/connections/${id}`).then(response => ({
      ...response,
      data: transformFromBackend(response.data)
    })),

  // Create new connection (uses inline endpoint)
  create: (data: CreateERPConnectionDTO) => {
    const backendData = transformToBackendInline(data)
    return api.post('/v1/erp/connections/inline', backendData).then(response => ({
      ...response,
      data: transformFromBackend(response.data)
    }))
  },

  // Update existing connection (uses inline endpoint)
  update: (id: string, data: UpdateERPConnectionDTO) => {
    // For updates, we need to merge with existing data
    // Since we're using inline endpoint, we need full data
    const backendData = transformToBackendInline(data as CreateERPConnectionDTO)
    console.log('ERP Update - Sending:', { id, backendData, holdingId: localStorage.getItem('holdingId') })
    return api.put(`/v1/erp/connections/inline/${id}`, backendData).then(response => ({
      ...response,
      data: transformFromBackend(response.data)
    }))
  },

  // Delete connection
  delete: (id: string) =>
    api.delete(`/v1/erp/connections/${id}`),

  // Test existing connection
  test: (id: string) =>
    api.post<TestConnectionResult>(`/v1/erp/connections/${id}/test`),

  // Test connection before saving (wizard step 5)
  testBeforeSave: (data: TestConnectionRequest) => {
    // Map frontend ERP types to backend ERPType enum
    const erpTypeMap: Record<string, string> = {
      'sap_s4hana_cloud': 'sap_s4hana_cloud',
      'sap_s4hana_private': 'sap_s4hana_op',
      'sap_r3_ecc': 'sap_r3_ecc',  // R/3 / ECC uses RFC only
      'sap_business_one': 'sap_b1',
      'dynamics_365_fo': 'dynamics_365',
      'dynamics_365_bc': 'dynamics_bc',
      'dynamics_365_ce': 'dynamics_365',
      'oracle_ebs': 'oracle_erp',
      'oracle_fusion': 'oracle_erp',
    }

    // Map frontend protocol to backend ConnectionProtocol enum
    const protocolMap: Record<string, string> = {
      'odata': 'odata_v4',
      'rfc': 'rfc',
      'soap': 'soap',
      'service_layer': 'rest',
    }

    // Transform frontend format to backend format
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const backendData: Record<string, any> = {
      erp_type: erpTypeMap[data.erp_system] || data.erp_system,
      protocol: protocolMap[data.protocol] || data.protocol,
      auth_type: 'basic',
    }

    // Include connection_id if editing existing connection (to use stored credentials)
    if (data.connection_id) {
      backendData.connection_id = data.connection_id
    }

    // Transform RFC config (frontend uses different field names)
    if (data.protocol === 'rfc' && data.rfc_config) {
      backendData.rfc_ashost = data.rfc_config.host
      backendData.rfc_sysnr = data.rfc_config.system_number
      backendData.sap_client = data.rfc_config.client
      backendData.username = data.rfc_config.username
      backendData.password = data.rfc_config.password
      backendData.rfc_lang = data.rfc_config.language || 'ES'
      backendData.base_url = `http://${data.rfc_config.host}` // Required by backend
      // SAProuter for connections via SAP router
      if (data.rfc_config.sap_router_string) {
        backendData.rfc_saprouter = data.rfc_config.sap_router_string
      }
    }

    // Transform OData config
    if (data.protocol === 'odata' && data.odata_config) {
      backendData.base_url = data.odata_config.base_url
      backendData.auth_type = data.odata_config.auth_type || 'basic'
      backendData.username = data.odata_config.username
      backendData.password = data.odata_config.password
      backendData.sap_client = data.odata_config.sap_client
      // OAuth2 fields
      if (data.odata_config.auth_type === 'oauth2') {
        backendData.client_id = data.odata_config.oauth_client_id
        backendData.client_secret = data.odata_config.oauth_client_secret
        backendData.token_url = `https://login.microsoftonline.com/${data.odata_config.oauth_tenant_id}/oauth2/v2.0/token`
      }
    }

    // Transform Service Layer config
    if (data.protocol === 'service_layer' && data.service_layer_config) {
      backendData.base_url = data.service_layer_config.base_url
      backendData.sap_b1_company_db = data.service_layer_config.company_db
      backendData.username = data.service_layer_config.username
      backendData.password = data.service_layer_config.password
    }

    return api.post('/v1/erp/connections/test', backendData).then(response => {
      // Transform backend response to frontend format
      const backendResult = response.data
      const result: TestConnectionResult = {
        success: backendResult.success,
        result_type: backendResult.success ? 'success' : 'unknown_error',
        message: backendResult.message,
        response_time_ms: backendResult.latency_ms,
        system_info: backendResult.server_version ? {
          version: backendResult.server_version,
        } : undefined,
        error_details: backendResult.error_details,
      }
      return { ...response, data: result }
    })
  },

  // Set connection as primary for empresa
  setPrimary: (id: string) =>
    api.post(`/v1/erp/connections/${id}/set-primary`),

  // Get connection logs/history
  getLogs: (id: string, options?: { limit?: number }) =>
    api.get(`/v1/erp/connections/${id}/logs`, { params: options }),
}
// =============================================================================
// DATA SOURCE API - Sprint 5.2
// =============================================================================

export const datasourcesApi = {
  // List all datasources for current holding
  list: (options?: { limit?: number; offset?: number; search?: string }) =>
    api.get<DataSourceListResponse>('/v1/datasources', { params: options }),

  // Get single datasource
  get: (id: string) =>
    api.get<DataSource>(`/v1/datasources/${id}`),

  // Get datasource with full details (entities + builders)
  getDetail: (id: string) =>
    api.get<DataSourceDetail>(`/v1/datasources/${id}/detail`),

  // Create datasource
  create: (data: CreateDataSourceDTO) =>
    api.post<DataSource>('/v1/datasources', data),

  // Update datasource
  update: (id: string, data: UpdateDataSourceDTO) =>
    api.patch<DataSource>(`/v1/datasources/${id}`, data),

  // Delete datasource
  delete: (id: string) =>
    api.delete(`/v1/datasources/${id}`),

  // Discover entities from ERP connection metadata
  discover: (datasourceId: string, forceRefresh = false) =>
    api.post<DiscoveryResponse>(`/v1/datasources/${datasourceId}/discover`, null, {
      params: { force_refresh: forceRefresh }
    }),

  // Get documentation
  getDocumentation: (datasourceId: string, format: 'full' | 'condensed' = 'full', regenerate = false) =>
    api.get<{ documentation: string }>(`/v1/datasources/${datasourceId}/documentation`, {
      params: { format, regenerate }
    }),

  // === ENTITIES ===

  // List entities for a datasource
  listEntities: (datasourceId: string, options?: { selected_only?: boolean }) =>
    api.get<DataSourceEntity[]>(`/v1/datasources/${datasourceId}/entities`, { params: options }),

  // Get single entity with builders
  getEntity: (datasourceId: string, entityId: string) =>
    api.get<DataSourceEntityWithBuilders>(`/v1/datasources/${datasourceId}/entities/${entityId}`),

  // Create entity
  createEntity: (datasourceId: string, data: CreateEntityDTO) =>
    api.post<DataSourceEntity>(`/v1/datasources/${datasourceId}/entities`, data),

  // Update entity
  updateEntity: (datasourceId: string, entityId: string, data: UpdateEntityDTO) =>
    api.patch<DataSourceEntity>(`/v1/datasources/${datasourceId}/entities/${entityId}`, data),

  // Delete entity
  deleteEntity: (datasourceId: string, entityId: string) =>
    api.delete(`/v1/datasources/${datasourceId}/entities/${entityId}`),

  // Bulk update entities (selection, methods)
  // Uses entity_set to identify entities (created by discovery)
  bulkUpdateEntities: (datasourceId: string, entities: Array<{ entity_set: string } & Partial<UpdateEntityDTO>>) =>
    api.patch(`/v1/datasources/${datasourceId}/entities/bulk`, { entities }),

  // === BUILDERS ===

  // List builders for an entity
  listBuilders: (datasourceId: string, entityId: string) =>
    api.get<DataSourceBuilder[]>(`/v1/datasources/${datasourceId}/entities/${entityId}/builders`),

  // Get single builder
  getBuilder: (datasourceId: string, entityId: string, builderId: string) =>
    api.get<DataSourceBuilder>(`/v1/datasources/${datasourceId}/entities/${entityId}/builders/${builderId}`),

  // Create builder
  createBuilder: (datasourceId: string, entityId: string, data: CreateBuilderDTO) =>
    api.post<DataSourceBuilder>(`/v1/datasources/${datasourceId}/entities/${entityId}/builders`, data),

  // Update builder
  updateBuilder: (datasourceId: string, entityId: string, builderId: string, data: UpdateBuilderDTO) =>
    api.patch<DataSourceBuilder>(`/v1/datasources/${datasourceId}/entities/${entityId}/builders/${builderId}`, data),

  // Delete builder
  deleteBuilder: (datasourceId: string, entityId: string, builderId: string) =>
    api.delete(`/v1/datasources/${datasourceId}/entities/${entityId}/builders/${builderId}`),

  // Get builder documentation
  getBuilderDoc: (datasourceId: string, entityId: string, builderId: string) =>
    api.get<{ documentation: string }>(`/v1/datasources/${datasourceId}/entities/${entityId}/builders/${builderId}/doc`),

  // Execute/test builder
  executeBuilder: (datasourceId: string, entityId: string, builderId: string, data?: ExecuteRequest) =>
    api.post<ExecuteResponse>(`/v1/datasources/${datasourceId}/entities/${entityId}/builders/${builderId}/execute`, data),

  // === OPERATIONS (Wizard v2) ===

  // List operations for a datasource
  listOperations: (datasourceId: string, options?: { entity_set?: string; http_method?: string; enabled_only?: boolean }) =>
    api.get<DataSourceOperation[]>(`/v1/datasources/${datasourceId}/operations`, { params: options }),

  // Get single operation
  getOperation: (operationId: string) =>
    api.get<DataSourceOperation>(`/v1/datasources/operations/${operationId}`),

  // Create operation
  createOperation: (datasourceId: string, data: CreateOperationDTO) =>
    api.post<DataSourceOperation>(`/v1/datasources/${datasourceId}/operations`, data),

  // Update operation
  updateOperation: (operationId: string, data: UpdateOperationDTO) =>
    api.patch<DataSourceOperation>(`/v1/datasources/operations/${operationId}`, data),

  // Delete operation
  deleteOperation: (operationId: string) =>
    api.delete(`/v1/datasources/operations/${operationId}`),

  // Bulk create operations
  bulkCreateOperations: (datasourceId: string, operations: CreateOperationDTO[]) =>
    api.post<DataSourceOperation[]>(`/v1/datasources/${datasourceId}/operations/bulk`, operations),

  // Test query against ERP (execute without saving)
  testQuery: (datasourceId: string, data: TestQueryRequest) =>
    api.post<TestQueryResponse>(`/v1/datasources/${datasourceId}/test-query`, data),

  // Fetch example record from entity (for POST/PATCH body templates)
  fetchExample: (datasourceId: string, entitySet: string) =>
    api.post<FetchExampleResponse>(`/v1/datasources/${datasourceId}/fetch-example`, null, {
      params: { entity_set: entitySet }
    }),

  // === EXPORT ===

  // Export all datasources as JSON/CSV
  export: (options?: { format?: 'json' | 'csv'; include_history?: boolean; active_only?: boolean }) =>
    api.get<DataSourceExportResponse>('/v1/datasources/export', { params: options }),
}

// =============================================================================
// PLATFORM PROMPTS API - Admin Prompt Management
// =============================================================================

export const promptsApi = {
  // List all platform prompts
  list: (options?: { category?: string; is_active?: boolean; search?: string; limit?: number; offset?: number }) =>
    api.get<PlatformPromptListResponse>('/v1/prompts/api/v1/platform-prompts', { params: options }),

  // Get single prompt by ID
  get: (id: string) =>
    api.get<PlatformPrompt>(`/v1/prompts/api/v1/platform-prompts/${id}`),

  // Get prompt by name
  getByName: (name: string) =>
    api.get<PlatformPrompt>(`/v1/prompts/api/v1/platform-prompts/by-name/${encodeURIComponent(name)}`),

  // Create new prompt
  create: (data: CreatePlatformPromptDTO) =>
    api.post<PlatformPrompt>('/v1/prompts/api/v1/platform-prompts', data),

  // INC-171/173: siembra los prompts base (mismo patron que el seed de PM).
  // Idempotente — hace upsert por nombre. Sin esto la unica via era correr
  // migrate_all_prompts.py dentro del pod, y en prod nunca se corrio: techdocs
  // usaba el prompt hardcodeado y /admin/prompts no tenia efecto.
  seedCore: () =>
    api.post<{ status: string; created: number; updated: number; total: number }>(
      '/v1/prompts/api/v1/platform-prompts/seed-core'
    ),

  // Update prompt (creates new version)
  update: (id: string, data: UpdatePlatformPromptDTO) =>
    api.put<PlatformPrompt>(`/v1/prompts/api/v1/platform-prompts/${id}`, data),

  // Delete prompt (soft delete)
  delete: (id: string) =>
    api.delete(`/v1/prompts/api/v1/platform-prompts/${id}`),

  // List versions for a prompt
  listVersions: (id: string, options?: { limit?: number; offset?: number }) =>
    api.get<PlatformPromptVersionListResponse>(`/v1/prompts/api/v1/platform-prompts/${id}/versions`, { params: options }),

  // Rollback to a specific version
  rollback: (id: string, version: number) =>
    api.post<PlatformPrompt>(`/v1/prompts/api/v1/platform-prompts/${id}/rollback/${version}`),

  // Admin tool: set default LLM provider for platform/tenant prompts.
  // No hardcodea el proveedor — el caller manda provider+model desde la UI
  // y el backend actualiza los registros. Requiere GAM Admin.
  setDefaultProvider: (body: {
    provider: string
    model: string
    temperature?: number
    max_tokens?: number
    force?: boolean
    scope?: 'platform' | 'tenant' | 'both'
    category?: string | null
    holding_id?: string | null
    reset_tenant_overrides?: boolean
  }) =>
    api.post<{
      success: boolean
      scope: string
      provider: string | null
      model: string | null
      force: boolean
      reset_tenant_overrides: boolean
      platform: {
        updated: number
        skipped: number
        total: number
        from_providers: Record<string, number>
      }
      tenant: {
        updated: number
        skipped: number
        total: number
        from_providers: Record<string, number>
      }
      note: string
    }>(`/v1/prompts/api/v1/platform-prompts/admin/set-default-provider`, body),
}

// Tenant Prompts API (holding-specific prompts)
export const tenantPromptsApi = {
  // List all prompts for a holding
  list: (holdingId: string, options?: { category?: string; is_active?: boolean; search?: string; limit?: number; offset?: number }) =>
    api.get<TenantPromptListResponse>(`/v1/prompts/api/v1/holdings/${holdingId}/prompts`, { params: options }),

  // Get single prompt by ID
  get: (holdingId: string, promptId: string) =>
    api.get<TenantPrompt>(`/v1/prompts/api/v1/holdings/${holdingId}/prompts/${promptId}`),

  // Create new prompt for holding
  create: (holdingId: string, data: CreateTenantPromptDTO) =>
    api.post<TenantPrompt>(`/v1/prompts/api/v1/holdings/${holdingId}/prompts`, data),

  // Update prompt
  update: (holdingId: string, promptId: string, data: UpdateTenantPromptDTO) =>
    api.put<TenantPrompt>(`/v1/prompts/api/v1/holdings/${holdingId}/prompts/${promptId}`, data),

  // Delete prompt (soft delete)
  delete: (holdingId: string, promptId: string, hardDelete?: boolean) =>
    api.delete(`/v1/prompts/api/v1/holdings/${holdingId}/prompts/${promptId}`, { params: { hard_delete: hardDelete } }),
}

// =============================================================================
// FIORI MOCKUPS API - Sprint 5.2 (Fiori Canvas Modal)
// =============================================================================

// Workflow status enum
export type FioriWorkflowStatus = 'draft' | 'in_review' | 'approved' | 'rejected' | 'published'

export interface FioriMockup {
  id: string
  holding_id: string
  tcode: string
  tcode_description?: string | null
  html_content?: string | null
  floorplan?: string | null
  screenshot?: string | null
  specification?: string | null
  config?: Record<string, unknown> | null
  created_at: string
  updated_at?: string | null
  status: string
  // Workflow fields
  workflow_status: FioriWorkflowStatus
  version: number
  created_by_id?: string | null
  updated_by_id?: string | null
  submitted_by_id?: string | null
  reviewed_by_id?: string | null
  submitted_at?: string | null
  reviewed_at?: string | null
  published_at?: string | null
  review_notes?: string | null
  // Computed permissions
  can_edit: boolean
  can_submit: boolean
  can_review: boolean
  can_publish: boolean
}

export interface FioriMockupSummary {
  id: string
  holding_id: string
  tcode: string
  tcode_description?: string | null
  floorplan?: string | null
  has_html: boolean
  has_screenshot: boolean
  has_specification: boolean
  created_at: string
  updated_at?: string | null
  // Workflow fields for list display
  workflow_status: FioriWorkflowStatus
  version: number
  created_by_id?: string | null
  reviewed_by_id?: string | null
  submitted_at?: string | null
  reviewed_at?: string | null
}

export interface FioriMockupUpsertRequest {
  tcode: string
  tcode_description?: string | null
  html_content?: string | null
  floorplan?: string | null
  screenshot?: string | null
  specification?: string | null
  config?: Record<string, unknown> | null
}

export interface FioriMockupCheckResponse {
  exists: boolean
  tcode: string
  mockup_id?: string | null
  updated_at?: string | null
  workflow_status?: FioriWorkflowStatus | null
}

// Workflow action types
export interface WorkflowActionResponse {
  success: boolean
  message: string
  mockup_id: string
  new_status: FioriWorkflowStatus
  version: number
  action_by?: string | null
  timestamp: string
}

export interface FioriMockupHistoryEntry {
  id: string
  mockup_id: string
  from_status?: string | null
  to_status: string
  action: string
  user_id?: string | null
  user_email?: string | null
  user_name?: string | null
  notes?: string | null
  version_snapshot?: number | null
  created_at: string
}

export interface FioriMockupHistoryResponse {
  mockup_id: string
  tcode: string
  current_status: FioriWorkflowStatus
  current_version: number
  history: FioriMockupHistoryEntry[]
}

export const fioriMockupsApi = {
  // List all mockups for current holding (lightweight summary)
  list: (options?: { page?: number; page_size?: number }) =>
    api.get<FioriMockupSummary[]>('/v1/fiori-mockups', { params: options }),

  // Check if mockup exists for tcode (lightweight check)
  check: (tcode: string) =>
    api.get<FioriMockupCheckResponse>(`/v1/fiori-mockups/check/${encodeURIComponent(tcode)}`),

  // Get mockup by tcode (full content)
  getByTcode: (tcode: string) =>
    api.get<FioriMockup>(`/v1/fiori-mockups/by-tcode/${encodeURIComponent(tcode)}`),

  // Get mockup by ID (full content)
  get: (id: string) =>
    api.get<FioriMockup>(`/v1/fiori-mockups/${id}`),

  // Upsert mockup (create or update by tcode)
  upsert: (data: FioriMockupUpsertRequest) =>
    api.put<FioriMockup>('/v1/fiori-mockups/upsert', data),

  // Delete mockup by ID
  delete: (id: string) =>
    api.delete(`/v1/fiori-mockups/${id}`),

  // Delete mockup by tcode
  deleteByTcode: (tcode: string) =>
    api.delete(`/v1/fiori-mockups/by-tcode/${encodeURIComponent(tcode)}`),

  // ========== WORKFLOW ACTIONS ==========

  // Submit mockup for review (draft -> in_review)
  submit: (mockupId: string, notes?: string) =>
    api.post<WorkflowActionResponse>(`/v1/fiori-mockups/${mockupId}/submit`, { notes }),

  // Approve or reject mockup (in_review -> approved/rejected)
  review: (mockupId: string, action: 'approve' | 'reject', notes: string) =>
    api.post<WorkflowActionResponse>(`/v1/fiori-mockups/${mockupId}/review`, { action, notes }),

  // Publish mockup (approved -> published)
  publish: (mockupId: string, notes?: string) =>
    api.post<WorkflowActionResponse>(`/v1/fiori-mockups/${mockupId}/publish`, { notes }),

  // Revert to draft (rejected -> draft)
  revert: (mockupId: string, notes?: string) =>
    api.post<WorkflowActionResponse>(`/v1/fiori-mockups/${mockupId}/revert`, { notes }),

  // Get workflow history
  getHistory: (mockupId: string) =>
    api.get<FioriMockupHistoryResponse>(`/v1/fiori-mockups/${mockupId}/history`),
}

// =============================================================================
// FIORI MOCKUP SHARING + EXTERNAL COMMENTS (INC-117, 2026-04-22)
// =============================================================================

export type CommentStatus =
  | 'pending'
  | 'accepted'
  | 'adjusted'
  | 'applied'
  | 'rejected'
  | 'deferred'
  | 'queued'

export interface FioriMockupShare {
  id: string
  mockup_id: string
  share_token: string
  share_url: string | null
  allow_comments: boolean
  is_revoked: boolean
  expires_at: string | null
  created_at: string
  created_by_id: string | null
  comment_count?: number | null
}

export interface FioriMockupComment {
  id: string
  mockup_id: string
  share_id: string | null
  author_name: string
  author_email: string | null
  comment_text: string
  screen_kind: string | null
  pin_x: number | null
  pin_y: number | null
  target_selector: string | null
  target_snippet: string | null
  target_tag: string | null
  comment_status: CommentStatus
  resolution_notes: string | null
  resolved_at: string | null
  applied_version: string | null
  created_at: string
}

export interface ShareCreateRequest {
  expires_in_hours?: number | null
  allow_comments?: boolean
}

export interface CommentApplyRequest {
  // 'accepted' | 'adjusted' → enqueue for batch apply (no LLM call now)
  // 'rejected' → terminal close
  // 'deferred' → postpone (non-terminal)
  // 'pending' → unqueue (only valid when current status=queued)
  action: 'accepted' | 'adjusted' | 'rejected' | 'deferred' | 'pending'
  adjusted_text?: string
  rejection_reason?: string
}

export interface CommentApplyResponse {
  comment: FioriMockupComment
  mockup_updated: boolean
  new_version: number | null
  error: string | null
}

export interface BatchApplyResponse {
  comments: FioriMockupComment[]
  mockup_updated: boolean
  applied_count: number
  new_version: number | null
  new_html: string | null
  new_specification: string | null
  error: string | null
}

export interface PublicSharePayload {
  mockup_id: string
  tcode: string
  tcode_description: string | null
  html_content: string | null
  floorplan: string | null
  specification: string | null
  allow_comments: boolean
  comments: FioriMockupComment[]
  owner_display_name: string | null
  share_expires_at: string | null
}

export interface PublicCommentCreateRequest {
  author_name: string
  author_email?: string
  comment_text: string
  screen_kind?: string
  pin_x?: number
  pin_y?: number
  target_selector?: string
  target_snippet?: string
  target_tag?: string
}

export const fioriSharingApi = {
  // Owner: shares CRUD
  listShares: (mockupId: string) =>
    api.get<FioriMockupShare[]>(`/v1/fiori-mockups/${mockupId}/shares`),

  createShare: (mockupId: string, data: ShareCreateRequest) =>
    api.post<FioriMockupShare>(`/v1/fiori-mockups/${mockupId}/shares`, data),

  revokeShare: (shareId: string) =>
    api.delete(`/v1/fiori-mockups/shares/${shareId}`),

  // Owner: comments
  listComments: (mockupId: string, statusFilter?: CommentStatus) =>
    api.get<FioriMockupComment[]>(`/v1/fiori-mockups/${mockupId}/comments`, {
      params: statusFilter ? { status_filter: statusFilter } : undefined,
    }),

  applyComment: (commentId: string, data: CommentApplyRequest) =>
    api.post<CommentApplyResponse>(
      `/v1/fiori-mockups/comments/${commentId}/apply`,
      data,
    ),

  // Batch: apply all queued comments for a mockup in a single LLM call.
  applyQueued: (mockupId: string) =>
    api.post<BatchApplyResponse>(
      `/v1/fiori-mockups/${mockupId}/comments/apply-queued`,
    ),

  // Owner admin: reset every comment of a given share back to 'pending'.
  resetShareComments: (shareId: string) =>
    api.post<FioriMockupComment[]>(
      `/v1/fiori-mockups/shares/${shareId}/reset-comments`,
    ),

  // Owner admin: reset every non-pending comment on a mockup to 'pending'.
  resetAllComments: (mockupId: string) =>
    api.post<FioriMockupComment[]>(
      `/v1/fiori-mockups/${mockupId}/comments/reset-all`,
    ),
}

// Public (no auth). Uses `fetch` directly — api axios instance injects auth headers.
// Comparte API_BASE_URL con el resto del cliente (VITE_API_URL).
export const fioriPublicApi = {
  async view(shareToken: string): Promise<PublicSharePayload> {
    const res = await fetch(
      `${API_BASE_URL}/public/fiori-mockups/shares/${encodeURIComponent(shareToken)}`,
    )
    if (!res.ok) {
      throw new Error(`Share not found (${res.status})`)
    }
    return res.json()
  },

  async addComment(
    shareToken: string,
    data: PublicCommentCreateRequest,
  ): Promise<FioriMockupComment> {
    const res = await fetch(
      `${API_BASE_URL}/public/fiori-mockups/shares/${encodeURIComponent(shareToken)}/comments`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    )
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}))
      throw new Error(
        errBody?.detail || `Failed to submit comment (${res.status})`,
      )
    }
    return res.json()
  },
}

// =============================================================================
// PROCESS MINING CONFIG API - Phase 4
// =============================================================================

import type {
  ProcessType,
  SAPTableConfig,
  TransactionTypeConfig,
  StatusMappingConfig,
  ReworkRuleConfig,
  KPIDefinitionConfig,
  SeedDataResponse,
  SeedDataSummary,
  ConfigSummaryResponse,
} from '@/types/pmconfig'

export const pmConfigApi = {
  // === SAP TABLES ===
  listSAPTables: (options?: { process_type?: ProcessType; status?: string }) =>
    api.get<SAPTableConfig[]>('/v1/pm/pm-config/sap-tables', { params: options }),

  getSAPTable: (id: string) =>
    api.get<SAPTableConfig>(`/v1/pm/pm-config/sap-tables/${id}`),

  // === TRANSACTION TYPES ===
  listTransactionTypes: (options?: { process_type?: ProcessType; status?: string }) =>
    api.get<TransactionTypeConfig[]>('/v1/pm/pm-config/transaction-types', { params: options }),

  getTransactionType: (id: string) =>
    api.get<TransactionTypeConfig>(`/v1/pm/pm-config/transaction-types/${id}`),

  // === STATUS MAPPINGS ===
  listStatusMappings: (options?: { process_type?: ProcessType; status?: string }) =>
    api.get<StatusMappingConfig[]>('/v1/pm/pm-config/status-mappings', { params: options }),

  // === REWORK RULES ===
  listReworkRules: (options?: { process_type?: ProcessType; severity?: string; status?: string }) =>
    api.get<ReworkRuleConfig[]>('/v1/pm/pm-config/rework-rules', { params: options }),

  getReworkRule: (id: string) =>
    api.get<ReworkRuleConfig>(`/v1/pm/pm-config/rework-rules/${id}`),

  // === KPI DEFINITIONS ===
  listKPIDefinitions: (options?: { process_type?: ProcessType; category?: string; status?: string }) =>
    api.get<KPIDefinitionConfig[]>('/v1/pm/pm-config/kpis', { params: options }),

  getKPIDefinition: (id: string) =>
    api.get<KPIDefinitionConfig>(`/v1/pm/pm-config/kpis/${id}`),

  // === SEED DATA ===
  populateSeedData: (processType: ProcessType, clearExisting?: boolean) =>
    api.post<SeedDataResponse>('/v1/pm/pm-config/seed', null, {
      params: { process_type: processType, clear_existing: clearExisting }
    }),

  populateAllSeedData: (clearExisting?: boolean) =>
    api.post<Record<string, SeedDataResponse>>('/v1/pm/pm-config/seed/all', null, {
      params: { clear_existing: clearExisting }
    }),

  // === SUMMARY ===
  getConfigSummary: () =>
    api.get<ConfigSummaryResponse>('/v1/pm/pm-config/summary'),

  getSeedDataSummary: () =>
    api.get<SeedDataSummary>('/v1/pm/pm-config/seed/summary'),
}

// === EXTRACTION API ===
export const extractionApi = {
  // Extraction Configs
  listConfigs: () =>
    api.get<ExtractionConfig[]>('/v1/pm/extraction-config/configs'),

  getConfig: (id: string) =>
    api.get<ExtractionConfig>(`/v1/pm/extraction-config/configs/${id}`),

  createConfig: (data: CreateExtractionConfigDTO) =>
    api.post<ExtractionConfig>('/v1/pm/extraction-config/configs', data),

  updateConfig: (id: string, data: Partial<CreateExtractionConfigDTO>) =>
    api.put<ExtractionConfig>(`/v1/pm/extraction-config/configs/${id}`, data),

  deleteConfig: (id: string) =>
    api.delete(`/v1/pm/extraction-config/configs/${id}`),

  // Trigger Extraction
  triggerExtraction: (data: TriggerExtractionRequest) =>
    api.post<TriggerExtractionResponse>('/v1/pm/pm/extraction/trigger', data),

  // Extraction Status & History
  getExtractionStatus: (historyId: string) =>
    api.get<ExtractionStatusResponse>(`/v1/pm/pm/extraction/${historyId}/status`),

  getExtractionHistory: (options?: { extraction_config_id?: string; status?: string; limit?: number }) =>
    api.get<ExtractionStatusResponse[]>('/v1/pm/pm/extraction/history', { params: options }),

  cancelExtraction: (historyId: string) =>
    api.post(`/v1/pm/pm/extraction/${historyId}/cancel`),

  // Event Log
  getEventLog: (options?: { case_id?: string; activity?: string; limit?: number; offset?: number }) =>
    api.get<EventLogResponse>('/v1/pm/pm/extraction/event-log', { params: options }),

  getEventLogStats: (extractionConfigId?: string) =>
    api.get<EventLogStats>('/v1/pm/pm/extraction/event-log/stats', {
      params: extractionConfigId ? { extraction_config_id: extractionConfigId } : undefined
    }),
}

// === BPMN DIAGRAMS API ===
export interface BPMNDiagramData {
  id: string
  holding_id: string
  name: string
  description?: string
  bpmn_xml: string
  extraction_config_id?: string
  process_type?: string
  created_by?: string
  created_at: string
  updated_at: string
  // Versioning metadata (present en list y get)
  current_version_id?: string | null
  current_version_number?: number | null
  versions_count?: number
  last_version_created_at?: string | null
  last_version_author_name?: string | null
}

// BPMN diagram versions (manual snapshots)
export interface BPMNDiagramVersionSummary {
  id: string
  diagram_id: string
  version_number: number
  name: string
  change_summary?: string | null
  author_id?: string | null
  author_name?: string | null
  created_at: string
}

export interface BPMNDiagramVersionDetail extends BPMNDiagramVersionSummary {
  bpmn_xml: string
}

export const bpmnDiagramsApi = {
  list: () =>
    api.get<BPMNDiagramData[]>('/v1/pm/diagrams'),

  get: (id: string) =>
    api.get<BPMNDiagramData>(`/v1/pm/diagrams/${id}`),

  create: (data: { name: string; description?: string; bpmn_xml: string; extraction_config_id?: string; process_type?: string }) =>
    api.post<BPMNDiagramData>('/v1/pm/diagrams', data),

  update: (id: string, data: { name?: string; description?: string; bpmn_xml?: string }) =>
    api.put<BPMNDiagramData>(`/v1/pm/diagrams/${id}`, data),

  delete: (id: string) =>
    api.delete(`/v1/pm/diagrams/${id}`),

  // Versioning
  listVersions: (id: string) =>
    api.get<BPMNDiagramVersionSummary[]>(`/v1/pm/diagrams/${id}/versions`),

  createVersion: (id: string, data: { change_summary?: string }) =>
    api.post<BPMNDiagramVersionSummary>(`/v1/pm/diagrams/${id}/versions`, data),

  getVersion: (id: string, versionId: string) =>
    api.get<BPMNDiagramVersionDetail>(`/v1/pm/diagrams/${id}/versions/${versionId}`),

  restoreVersion: (id: string, versionId: string) =>
    api.post<BPMNDiagramData>(`/v1/pm/diagrams/${id}/versions/${versionId}/restore`),
}

// Extraction Types
export interface ExtractionConfig {
  id: string
  holding_id: string
  empresa_id?: string
  name: string
  description?: string
  process_type: ProcessType
  erp_connection_id: string
  company_codes?: string[]
  date_from?: string
  date_to?: string
  is_active: boolean
  last_extraction_at?: string
  created_at: string
  updated_at?: string
}

export interface CreateExtractionConfigDTO {
  name: string
  description?: string
  process_type: ProcessType
  erp_connection_id: string
  company_codes?: string[]
  date_from?: string
  date_to?: string
  is_active?: boolean
}

export interface TriggerExtractionRequest {
  extraction_config_id: string
  incremental?: boolean
}

export interface TriggerExtractionResponse {
  extraction_history_id: string
  status: string
  message: string
}

export interface ExtractionStatusResponse {
  extraction_history_id: string
  extraction_config_id: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  started_at?: string
  completed_at?: string
  events_extracted: number
  cases_extracted: number
  activities_found: number
  errors_count: number
  duration_seconds?: number
  error_message?: string
  progress_percent?: number
}

// === Unified extraction progress (EXTRACCION_ESTANDAR.md) ===
// Canonical contract: GET /v1/extraction/runs/{run_id}/progress
// PM mount: /api/v1/pm/extraction/runs/{run_id}/progress
export type ExtractionProgressModule = 'pm' | 'cdhdr' | 'sal' | 'cts' | 'odata'
export type ExtractionProgressStatus =
  | 'pending'
  | 'running'
  | 'completed'
  // INC-185: la corrida terminó pero NO pudo demostrar que trajo todo. Es un
  // estado TERMINAL, no intermedio: sin este caso el front lo trataba como
  // desconocido y lo pintaba con el spinner de "en curso".
  | 'partial'
  | 'failed'
  | 'cancelled'

export interface ExtractionProgressData {
  run_id: string
  module: ExtractionProgressModule
  status: ExtractionProgressStatus
  progress_pct: number
  current_step: string | null
  current_target: string | null
  records_processed: number
  records_total_estimate: number | null
  records_per_second: number | null
  started_at: string
  last_heartbeat: string
  completed_at: string | null
  elapsed_seconds: number
  eta_seconds: number | null
  error_message: string | null
  metadata: Record<string, unknown>
}

// Per-module URL builder.
// - PM lives behind the process-mining service (`/v1/pm/...`).
// - CDHDR/SAL/CTS/OData live behind the erp_connector service (`/v1/erp/...`)
//   and share the canonical `/extraction/runs/{run_id}/progress` endpoint,
//   which disambiguates with the `module` query param (F5/F6/F7).
function buildProgressUrl(runId: string, module: ExtractionProgressModule): string {
  switch (module) {
    case 'pm':
      return `/v1/pm/pm/extraction/runs/${encodeURIComponent(runId)}/progress`
    case 'cdhdr':
    case 'sal':
    case 'cts':
    case 'odata':
      return `/v1/erp/extraction/runs/${encodeURIComponent(runId)}/progress`
    default:
      return `/v1/${module}/extraction/runs/${encodeURIComponent(runId)}/progress`
  }
}

export const extractionProgressApi = {
  /** Fetch the current progress snapshot for a run. */
  getProgress: (
    runId: string,
    module: ExtractionProgressModule = 'pm',
  ) =>
    api.get<ExtractionProgressData>(buildProgressUrl(runId, module), {
      params: { module },
    }),
}

export interface EventLogResponse {
  events: EventLogEntry[]
  count: number
  limit: number
  offset: number
}

export interface EventLogEntry {
  id: string
  case_id: string
  activity: string
  start_timestamp: string
  resource?: string
  document_number?: string
  rework_flag: boolean
  rework_type?: string
  is_automated: boolean
  source_table?: string
}

export interface EventLogStats {
  total_events: number
  total_cases: number
  total_activities: number
  rework_events: number
  rework_rate: number
  automated_events: number
  automation_rate: number
}

// =============================================================================
// PROCESS INTELLIGENCE API - Phase PI
// =============================================================================

// Process Intelligence Types
export interface ConformanceViolation {
  rule_id: string
  rule_name: string
  rule_type: 'sequence' | 'mandatory' | 'forbidden' | 'timing'
  severity: 'critical' | 'high' | 'medium' | 'low'
  description: string
  case_id: string
  details: Record<string, unknown>
  timestamp?: string
}

export interface ConformanceResult {
  total_cases: number
  conformant_cases: number
  non_conformant_cases: number
  conformance_rate: number
  conformance_rate_pct?: number
  violations_by_type?: Record<string, number>       // alias for violations_by_criticality
  violations_by_criticality?: Record<string, number>
  violations_by_severity?: Record<string, number>   // may not be present
  violations: ConformanceViolation[]
  rules_checked: number
  rule_summary?: ConformanceRuleSummary[]
  // F0.6: ¿hay proceso que medir? Si no, la tasa no se muestra.
  // F0.31: la misma definición que la tarjeta de minabilidad (plan v6). `reason`
  // es la primera condición que falló; `condiciones` trae valor y umbral.
  process_signal?: {
    median_events_per_case: number
    total_variants: number
    sufficient: boolean
    reason:
      | 'casos_cortos' | 'pocas_actividades' | 'variante_unica' | 'variante_dominante'
      | 'sin_fecha_propia' | 'sin_casos' | 'sin_volumen' | 'movements_analysis' | null
    fallas?: string[]
    // F0.20 (Ignacio, 15-10): 50 casos o menos se nombran aparte, no "no minable".
    veredicto?: 'minable' | 'no_minable' | 'sin_volumen' | 'sin_casos'
    condiciones?: { codigo: string; valor: number; umbral: number; cumple: boolean; decide: boolean }[]
    muestra_chica?: boolean
  }
}

export interface ConformanceRule {
  rule_id: string
  name: string
  rule_type: string
  severity: string
  description: string
  process_type: string
  activities?: string[]
  sequence?: string[]
  max_duration_hours?: number
}

export interface ConformanceRuleSummary {
  rule_id: string
  name: string
  rule_type: string
  criticality: string
  description: string
  violations_count: number
  cases_affected: number
  violation_rate: number
}

export interface SoDViolation {
  case_id: string
  user: string
  rule_id: string
  rule_name: string
  conflicting_activities: string[]
  risk_level: string
  timestamps: string[]
}

export interface SoDRuleSummary {
  rule_id: string
  name: string
  risk_level: string
  description: string
  incompatible_activities: string[]
  violations_count: number
  cases_affected: number
  users_involved: number
}

export interface SoDResult {
  total_cases_analyzed?: number       // alias: total_cases
  total_cases?: number
  cases_with_violations: number
  total_violations: number
  sod_violation_rate?: number         // alias: violation_rate
  violation_rate?: number
  violations_by_rule?: Record<string, number>
  violations_by_risk?: Record<string, number>
  top_violators?: Array<{ user: string; violation_count: number }>
  high_risk_users?: Array<{ user: string; violations: number; risk_level: string }>
  violations: SoDViolation[]
  rule_summary?: SoDRuleSummary[]
}

export interface SoDMatrixRule {
  rule_id: string
  name: string
  activity_a: string
  activity_b: string
  risk_level: string
  description: string
  process_type: string
}

export interface RCASegment {
  segment_id: string
  rule: string
  size: number
  target_rate: number
  lift: number
  support: number
  confidence: number
}

export interface RCAResult {
  target_variable: string
  threshold: number
  total_cases: number
  positive_cases: number
  positive_rate: number
  feature_importance: Record<string, number>
  segments: RCASegment[]
  tree_depth: number
  insights: string[]
}

export interface TargetVariableInfo {
  id: string
  name: string
  description: string
  type: 'duration' | 'count' | 'boolean' | 'rate'
  default_threshold?: number
}

export interface ProcessIntelligenceKPIs {
  cycle_time_median: number
  cycle_time_p95: number
  rework_rate: number
  touchless_rate: number
  waiting_time_avg: number
  total_cases: number
  total_events: number
  unique_activities: number
}

export interface FullAnalysisResult {
  kpis: ProcessIntelligenceKPIs
  conformance: ConformanceResult
  sod: SoDResult
  bottlenecks: Array<{
    activity: string
    avg_wait_time: number
    frequency: number
    bottleneck_score: number
  }>
  top_variants: Array<{
    variant_id: string
    activities: string[]
    case_count: number
    percentage: number
    avg_duration: number
  }>
}

export const processIntelligenceApi = {
  // === CONFORMANCE ===
  getConformance: (configId: string, processType = 'p2p') =>
    api.get<ConformanceResult>(`/v1/pm/pm/intelligence/${configId}/conformance`, {
      params: { process_type: processType }
    }),

  getConformanceRules: (configId: string, processType = 'p2p') =>
    api.get<{ rules: ConformanceRule[] }>(`/v1/pm/pm/intelligence/${configId}/conformance/rules`, {
      params: { process_type: processType }
    }),

  // === SEGREGATION OF DUTIES (SoD) ===
  getSoD: (configId: string, processType = 'p2p') =>
    api.get<SoDResult>(`/v1/pm/pm/intelligence/${configId}/sod`, {
      params: { process_type: processType }
    }),

  getSoDMatrix: (configId: string, processType = 'p2p') =>
    api.get<{ matrix: SoDMatrixRule[] }>(`/v1/pm/pm/intelligence/${configId}/sod/matrix`, {
      params: { process_type: processType }
    }),

  // === ROOT CAUSE ANALYSIS (RCA) ===
  getRCA: (configId: string, targetVariable: string, threshold?: number) =>
    api.get<RCAResult>(`/v1/pm/pm/intelligence/${configId}/rca`, {
      params: { target_variable: targetVariable, threshold }
    }),

  getRCATargets: (configId: string) =>
    api.get<{ targets: TargetVariableInfo[] }>(`/v1/pm/pm/intelligence/${configId}/rca/targets`),

  getRCASummary: (configId: string) =>
    api.get<{ summaries: Record<string, RCAResult> }>(`/v1/pm/pm/intelligence/${configId}/rca/summary`),

  // === KPIs ===
  getKPIs: (configId: string) =>
    api.get<ProcessIntelligenceKPIs>(`/v1/pm/pm/intelligence/${configId}/kpis`),

  // === FULL ANALYSIS ===
  getFullAnalysis: (configId: string, processType = 'p2p') =>
    api.get<FullAnalysisResult>(`/v1/pm/pm/intelligence/${configId}/full-analysis`, {
      params: { process_type: processType }
    }),
}

// =============================================================================
// BUSINESS PROCESSES API - Sprint 5.3
// =============================================================================

import type {
  BusinessProcess,
  BusinessProcessDetail,
  BusinessProcessListResponse,
  CreateBusinessProcessDTO,
  UpdateBusinessProcessDTO,
  ProcessStepWithFields,
  CreateProcessStepDTO,
  UpdateProcessStepDTO,
  CreateStepFieldDTO,
  UpdateStepFieldDTO,
  StepField,
  ProcessTemplate,
  ProcessExecution,
  ProcessExecutionListResponse,
  CreateExecutionDTO,
  ExecuteProcessRequest,
  ExecuteProcessResponse,
  ExecuteStepRequest,
  ExecuteStepResponse,
  ValidateStepRequest,
  ValidateStepResponse,
  AnalyzeJsonRequest,
  AnalyzeJsonResponse,
} from '@/types/business-process'

export const businessProcessApi = {
  // === PROCESSES CRUD ===

  // List all business processes for current holding
  list: (options?: { limit?: number; offset?: number; search?: string; process_type?: string }) =>
    api.get<BusinessProcessListResponse>('/v1/business-processes', { params: options }),

  // Get single process (summary)
  get: (id: string) =>
    api.get<BusinessProcess>(`/v1/business-processes/${id}`),

  // Get process with full details (steps + fields)
  getDetail: (id: string) =>
    api.get<BusinessProcessDetail>(`/v1/business-processes/${id}/detail`),

  // Create process
  create: (data: CreateBusinessProcessDTO) =>
    api.post<BusinessProcess>('/v1/business-processes', data),

  // Update process
  update: (id: string, data: UpdateBusinessProcessDTO) =>
    api.patch<BusinessProcess>(`/v1/business-processes/${id}`, data),

  // Delete process
  delete: (id: string) =>
    api.delete(`/v1/business-processes/${id}`),

  // === STEPS ===

  // List steps for a process
  listSteps: (processId: string) =>
    api.get<ProcessStepWithFields[]>(`/v1/business-processes/${processId}/steps`),

  // Get single step with fields
  getStep: (processId: string, stepId: string) =>
    api.get<ProcessStepWithFields>(`/v1/business-processes/${processId}/steps/${stepId}`),

  // Create step
  createStep: (processId: string, data: CreateProcessStepDTO) =>
    api.post<ProcessStepWithFields>(`/v1/business-processes/${processId}/steps`, data),

  // Update step
  updateStep: (processId: string, stepId: string, data: UpdateProcessStepDTO) =>
    api.patch<ProcessStepWithFields>(`/v1/business-processes/${processId}/steps/${stepId}`, data),

  // Delete step
  deleteStep: (processId: string, stepId: string) =>
    api.delete(`/v1/business-processes/${processId}/steps/${stepId}`),

  // Bulk create steps
  bulkCreateSteps: (processId: string, steps: CreateProcessStepDTO[]) =>
    api.post<ProcessStepWithFields[]>(`/v1/business-processes/${processId}/steps/bulk`, { steps }),

  // === FIELDS ===

  // List fields for a step
  listFields: (processId: string, stepId: string) =>
    api.get<StepField[]>(`/v1/business-processes/${processId}/steps/${stepId}/fields`),

  // Create field
  createField: (processId: string, stepId: string, data: CreateStepFieldDTO) =>
    api.post<StepField>(`/v1/business-processes/${processId}/steps/${stepId}/fields`, data),

  // Update field
  updateField: (processId: string, stepId: string, fieldId: string, data: UpdateStepFieldDTO) =>
    api.patch<StepField>(`/v1/business-processes/${processId}/steps/${stepId}/fields/${fieldId}`, data),

  // Delete field
  deleteField: (processId: string, stepId: string, fieldId: string) =>
    api.delete(`/v1/business-processes/${processId}/steps/${stepId}/fields/${fieldId}`),

  // Bulk create fields
  bulkCreateFields: (processId: string, stepId: string, fields: CreateStepFieldDTO[]) =>
    api.post<StepField[]>(`/v1/business-processes/${processId}/steps/${stepId}/fields/bulk`, { fields }),

  // === TEMPLATES ===

  // List all templates
  listTemplates: (options?: { process_type?: string }) =>
    api.get<ProcessTemplate[]>('/v1/business-processes/templates', { params: options }),

  // Get template by ID
  getTemplate: (id: string) =>
    api.get<ProcessTemplate>(`/v1/business-processes/templates/${id}`),

  // === EXECUTION ===

  // Execute full process
  execute: (processId: string, data: ExecuteProcessRequest) =>
    api.post<ExecuteProcessResponse>(`/v1/business-processes/${processId}/execute`, data),

  // Execute single step
  executeStep: (processId: string, data: ExecuteStepRequest) =>
    api.post<ExecuteStepResponse>(`/v1/business-processes/${processId}/execute-step`, data),

  // Get execution history for process
  listExecutions: (processId: string, options?: { limit?: number; offset?: number; status?: string }) =>
    api.get<ProcessExecutionListResponse>(`/v1/business-processes/${processId}/executions`, { params: options }),

  // Get single execution
  getExecution: (processId: string, executionId: string) =>
    api.get<ProcessExecution>(`/v1/business-processes/${processId}/executions/${executionId}`),

  // Create execution (start new)
  createExecution: (processId: string, data?: CreateExecutionDTO) =>
    api.post<ProcessExecution>(`/v1/business-processes/${processId}/executions`, data || {}),

  // Cancel execution
  cancelExecution: (processId: string, executionId: string) =>
    api.post(`/v1/business-processes/${processId}/executions/${executionId}/cancel`),

  // === VALIDATION ===

  // Validate step configuration
  validateStep: (processId: string, data: ValidateStepRequest) =>
    api.post<ValidateStepResponse>(`/v1/business-processes/${processId}/validate-step`, data),

  // Test ERP connection for process
  testConnection: (processId: string) =>
    api.post(`/v1/business-processes/${processId}/test-connection`),

  // === FIORI APP GENERATION ===

  // Generate Fiori app (triggers SSE stream, call this for manual trigger)
  generateFioriApp: (processId: string, options?: { regenerate?: boolean; custom_prompt?: string }) =>
    api.post(`/v1/business-processes/${processId}/generate-app`, options),

  // Get Fiori app preview HTML
  getFioriAppPreview: (processId: string) =>
    api.get<{ html: string; version: number }>(`/v1/business-processes/${processId}/app-preview`),

  // === JSON ANALYZER ===

  // Analyze JSON payload to detect fields and structure
  analyzeJson: (data: AnalyzeJsonRequest) =>
    api.post<AnalyzeJsonResponse>('/v1/business-processes/analyze-json', data),
}

// =============================================================================
// AUDIT EXPLORER API - Consulta de SAL almacenado
// =============================================================================

export interface AuditEvent {
  id: string
  timestamp: string
  sap_user: string
  event_id: string
  event_class?: string
  event_text?: string
  tcode?: string
  program?: string
  terminal?: string
  sap_client?: string
}

export interface AuditEventsResponse {
  events: AuditEvent[]
  total_count: number
  page: number
  page_size: number
  filters_applied: Record<string, unknown>
}

export interface UserStatistics {
  sap_user: string
  event_count: number
  unique_tcodes: number
  first_activity?: string
  last_activity?: string
  top_tcodes: Array<{ tcode: string; count: number }>
}

export interface TcodeStatistics {
  tcode: string
  execution_count: number
  unique_users: number
  unique_programs: string[]
}

export interface HeatmapCell {
  user: string
  tcode: string
  count: number
}

export interface HeatmapResponse {
  cells: HeatmapCell[]
  users: string[]
  tcodes: string[]
  max_count: number
}

export interface NgramEntry {
  sequence: string[]
  count: number
  users: string[]
}

export interface NgramsResponse {
  ngrams: NgramEntry[]
  n: number
  total_sequences: number
}

export interface EventTypeInfo {
  event_id: string
  count: number
  description: string
  is_critical: boolean
}

// ERP Connector base URL
const ERP_CONNECTOR_URL = import.meta.env.VITE_ERP_CONNECTOR_URL || `${import.meta.env.VITE_API_URL || '/api'}/v1/erp`

// Create separate axios instance for ERP Connector
const erpApi = applyAuthInterceptors(
  axios.create({
    baseURL: ERP_CONNECTOR_URL,
    headers: { 'Content-Type': 'application/json' },
  })
)

export const auditExplorerApi = {
  // Get audit events with filters
  getEvents: (params?: {
    connection_id?: string
    date_from?: string
    date_to?: string
    users?: string
    tcodes?: string
    event_types?: string
    page?: number
    page_size?: number
  }) => erpApi.get<AuditEventsResponse>('/audit/events', { params }),

  // Get user statistics
  getUserStats: (params?: {
    connection_id?: string
    date_from?: string
    date_to?: string
    limit?: number
  }) => erpApi.get<{ users: UserStatistics[]; total_users: number }>('/audit/users', { params }),

  // Get tcode statistics
  getTcodeStats: (params?: {
    connection_id?: string
    date_from?: string
    date_to?: string
    limit?: number
  }) => erpApi.get<{ tcodes: TcodeStatistics[]; total_tcodes: number }>('/audit/tcodes', { params }),

  // Get heatmap data
  getHeatmap: (params?: {
    connection_id?: string
    date_from?: string
    date_to?: string
    top_users?: number
    top_tcodes?: number
  }) => erpApi.get<HeatmapResponse>('/audit/heatmap', { params }),

  // Get n-grams
  getNgrams: (params?: {
    connection_id?: string
    date_from?: string
    date_to?: string
    n?: number
    min_count?: number
    limit?: number
  }) => erpApi.get<NgramsResponse>('/audit/ngrams', { params }),

  // Get event types
  getEventTypes: (connection_id?: string) =>
    erpApi.get<{ event_types: EventTypeInfo[]; total_events: number }>('/audit/event-types', {
      params: connection_id ? { connection_id } : undefined,
    }),
}

// =============================================================================
// USER ACTIVITY ANALYTICS API - SAL + PM Overlay
// =============================================================================

export interface UserActivityListItem {
  sap_user: string
  event_count: number
  unique_tcodes: number
  first_activity?: string
  last_activity?: string
  risk_score: number
  risk_factors: string[]
  department?: string
}

export interface HourlyHeatmapCell {
  day_of_week: number
  hour: number
  count: number
}

export interface WorkingHoursResponse {
  cells: HourlyHeatmapCell[]
  max_count: number
  total_events: number
}

export interface UserProfileData {
  sap_user: string
  event_count: number
  unique_tcodes: number
  first_activity?: string
  last_activity?: string
  department?: string
  user_group?: string
  is_locked?: boolean
  risk_score: number
  risk_factors: string[]
  heatmap: HourlyHeatmapCell[]
  top_tcodes: Array<{ tcode: string; count: number }>
  terminals: string[]
  critical_events: Array<Record<string, unknown>>
}

export interface DepartmentStats {
  department: string
  user_count: number
  event_count: number
  avg_events_per_user: number
  after_hours_pct: number
  critical_event_count: number
  unique_tcodes: number
}

export interface AnomalyEntry {
  rule_id: string
  rule_name: string
  severity: string
  sap_user: string
  description: string
  event_count: number
  sample_events: Array<Record<string, unknown>>
}

export interface SessionEvent {
  timestamp: string
  event_id: string
  tcode?: string
  program?: string
  terminal?: string
  event_text?: string
}

export interface SessionBlock {
  session_start: string
  session_end: string
  duration_minutes: number
  event_count: number
  terminal?: string
  events: SessionEvent[]
}

export interface PMCrossRefEntry {
  pm_event_id: string
  case_id: string
  activity: string
  pm_timestamp: string
  pm_user: string
  sal_trail: Array<Record<string, unknown>>
  match_type?: string
  confidence?: number
}

export interface ComparativeUserEntry {
  sap_user: string
  execution_count: number
  avg_duration_minutes?: number
  unique_terminals: number
  after_hours_count: number
  failed_auth_count: number
  is_outlier: boolean
}

type AnalyticsFilterParams = {
  connection_id?: string
  date_from?: string
  date_to?: string
}

export const userActivityApi = {
  getUserList: (params?: AnalyticsFilterParams & { limit?: number }) =>
    erpApi.get<{ users: UserActivityListItem[]; total_users: number }>('/audit/analytics/user-list', { params }),

  getUserProfile: (sapUser: string, params?: AnalyticsFilterParams) =>
    erpApi.get<UserProfileData>(`/audit/analytics/user-profile/${encodeURIComponent(sapUser)}`, { params }),

  getWorkingHours: (params?: AnalyticsFilterParams) =>
    erpApi.get<WorkingHoursResponse>('/audit/analytics/working-hours', { params }),

  getDepartmentAnalysis: (params?: AnalyticsFilterParams) =>
    erpApi.get<{ departments: DepartmentStats[]; total_departments: number }>('/audit/analytics/department-analysis', { params }),

  getAnomalies: (params?: AnalyticsFilterParams & { severity?: string; rule?: string }) =>
    erpApi.get<{ anomalies: AnomalyEntry[]; total_anomalies: number; by_severity: Record<string, number> }>('/audit/analytics/anomalies', { params }),

  getSessionTimeline: (sapUser: string, params?: AnalyticsFilterParams) =>
    erpApi.get<{ sap_user: string; sessions: SessionBlock[]; total_sessions: number }>(`/audit/analytics/session-timeline/${encodeURIComponent(sapUser)}`, { params }),

  getPMCrossRef: (params?: AnalyticsFilterParams & { case_id?: string; page?: number; page_size?: number }) =>
    erpApi.get<{ entries: PMCrossRefEntry[]; total_entries: number; page: number; page_size: number }>('/audit/analytics/pm-crossref', { params }),

  getComparative: (activity: string, params?: AnalyticsFilterParams) =>
    erpApi.get<{ activity: string; users: ComparativeUserEntry[]; total_users: number; avg_execution_count: number; stddev_execution_count: number }>('/audit/analytics/comparative', { params: { ...params, activity } }),
}

// =============================================================================
// SAP AUTHORIZATION EXTRACTOR API - Extract SAL from SAP
// =============================================================================

export interface SALExtractionRequest {
  date_from?: string
  date_to?: string
  users?: string[]
  max_rows?: number
}

// Discriminated union for SAL extraction methods
export type SALExtractionMethod =
  | 'RSAU_READ_FILE'       // SM20 reader - reads kernel SAL .AUD files
  | 'RSAU_READ_LOG'        // Legacy file-based SAL
  | 'RSAU_API_GET_LOG_DATA' // Kernel SAL via API (requires Z wrapper)
  | 'Z_ALDO_READ_SAL'      // Custom Z wrapper (best option)
  | 'RSAU_API_GET_ALERTS'  // Alert mode (near real-time)
  | 'RSAU_LOG_TABLE'       // DB logging (RFC_READ_TABLE on RSAU_LOG)
  | 'CDHDR'                // Change Documents fallback
  | 'SM20_EXPORT'          // Manual SM20 export

export interface SALExtractionResponse {
  status: string
  extraction_id?: string
  connection_id?: string
  records_extracted: number
  records_stored: number
  duration_seconds?: number
  date_range?: {
    from: string
    to: string
  }
  error?: string
  error_message?: string
  warnings?: string[]
  extraction_method?: SALExtractionMethod | string
}

export interface SALExtractionHistory {
  id: string
  connection_id: string
  connection_name?: string
  date_from: string
  date_to: string
  rows_extracted: number
  rows_stored: number
  status: string
  started_at: string
  completed_at?: string
  error?: string
}

export const sapAuthApi = {
  // Extract SAL and store in PostgreSQL (legacy synchronous path).
  // F7: pass wait=true so the backend preserves the old contract and returns
  // the full SALExtractionResponse in-line.
  extractSAL: (connectionId: string, data: SALExtractionRequest) =>
    erpApi.post<SALExtractionResponse>('/sap-auth/store/sal', data, {
      params: { connection_id: connectionId, wait: true },
    }),

  /**
   * F7 fire-and-forget: returns `{ run_id, status: 'started', module: 'sal' }`.
   * Frontend should mount `<ExtractionProgressPanel runId={run_id} module="sal" />`
   * for the unified progress panel.
   */
  startSALExtraction: (connectionId: string, data: SALExtractionRequest) =>
    erpApi.post<{ run_id: string; status: string; module: string }>(
      '/sap-auth/store/sal',
      data,
      { params: { connection_id: connectionId } },
    ),

  listSALHistory: (limit: number = 50) =>
    erpApi.get<{ items: Array<Record<string, unknown>>; count: number }>(
      '/sap-auth/sal/history',
      { params: { limit } },
    ),

  getSALHistoryItem: (runId: string) =>
    erpApi.get<Record<string, unknown>>(
      `/sap-auth/sal/history/${encodeURIComponent(runId)}`,
    ),

  // Get extraction history (legacy endpoint)
  getHistory: (connectionId?: string) =>
    erpApi.get<{ history: SALExtractionHistory[] }>('/sap-auth/history', {
      params: connectionId ? { connection_id: connectionId } : undefined,
    }),

  // Get system info (capabilities check)
  getSystemInfo: (connectionId: string) =>
    erpApi.get<{ version: string; type: string; capabilities: string[] }>('/sap-auth/system-info', {
      params: { connection_id: connectionId },
    }),

  // Get SAL event types reference
  getEventTypes: () =>
    erpApi.get<Record<string, string>>('/sap-auth/sal/event-types'),
}

// =============================================================================
// TECHNICAL CATALOGS API - Persistent catalogs for TechDocs
// =============================================================================

export interface TechnicalCatalog {
  id: string
  holding_id: string
  connection_id: string
  catalog_type: string
  catalog_type_label: string
  system_id: string | null
  extracted_at: string | null
  extracted_by: string | null
  extraction_duration_ms: number | null
  items: Array<Record<string, unknown>>
  items_count: number
  extraction_filters: Record<string, unknown>
  status: string
  created_at: string | null
  updated_at: string | null
}

export interface CatalogSummary {
  id: string
  holding_id: string
  connection_id: string
  catalog_type: string
  catalog_type_label: string
  system_id: string | null
  extracted_at: string | null
  extracted_by: string | null
  extraction_duration_ms: number | null
  items_count: number
  extraction_filters: Record<string, unknown>
  status: string
  created_at: string | null
  updated_at: string | null
}

export const catalogsApi = {
  // List all catalogs for current holding (summary only, no items)
  list: (connectionId?: string) =>
    erpApi.get<{ catalogs: CatalogSummary[]; catalog_types: Record<string, string> }>(
      '/abap-docs/catalogs',
      { params: connectionId ? { connection_id: connectionId } : undefined }
    ),

  // Get catalog metadata (no items) or search items server-side
  get: (catalogType: string, connectionId?: string, search?: string) =>
    erpApi.get<TechnicalCatalog>(
      `/abap-docs/catalogs/${catalogType}`,
      { params: { connection_id: connectionId, ...(search ? { search, limit: 1000 } : {}) } }  // tope del backend (le=1000)
    ),

  // Refresh (re-extract from SAP)
  refresh: (catalogType: string, connectionId: string, filters?: {
    custom_only?: boolean
    language?: string
    max_rows?: number
  }) =>
    erpApi.post<TechnicalCatalog>(
      `/abap-docs/catalogs/${catalogType}/refresh`,
      null,
      { params: { connection_id: connectionId, ...filters } }
    ),

  // Delete catalog
  delete: (catalogType: string, connectionId?: string) =>
    erpApi.delete(
      `/abap-docs/catalogs/${catalogType}`,
      { params: connectionId ? { connection_id: connectionId } : undefined }
    ),
}

// =============================================================================
// SOURCE EXTRACTION API - Extraccion masiva desacoplada de IA
// =============================================================================

export interface ExtractionJob {
  id: string
  holding_id: string
  connection_id: string
  object_type: string
  object_keys: string[]
  extraction_options: Record<string, unknown>
  execution_mode: string
  scheduled_at: string | null
  status: string
  progress_pct: number
  progress_message: string | null
  objects_total: number
  objects_completed: number
  objects_failed: number
  errors: string[]
  system_id: string | null
  started_at: string | null
  completed_at: string | null
  processing_time_ms: number | null
  created_at: string | null
  updated_at: string | null
}

export interface StoredSource {
  id: string
  holding_id: string
  connection_id: string
  extraction_job_id: string | null
  object_key: string
  object_type: string
  program_name: string | null
  object_description: string | null
  total_objects: number
  total_lines: number
  has_dynpros: boolean
  has_functions: boolean
  has_classes: boolean
  system_id: string | null
  extracted_at: string | null
  version: number
  is_current: boolean
  documentation_status: string
  last_documented_at: string | null
  documentation_id: string | null
  /** Motivo del ultimo fallo de documentacion. Solo con documentation_status === 'error'. */
  documentation_error: string | null
  /** F5_21d (#616): fecha en que el borrado por plazo (30 dias) vacio el codigo; null = sigue guardado. */
  source_purged_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface StoredSourceDetail extends StoredSource {
  extraction_bundle: Record<string, unknown>
}

export interface SourceVersion {
  id: string
  version: number
  is_current: boolean
  extracted_at: string | null
  total_lines: number
  documentation_status: string
  created_at: string | null
}

export interface GenerateFromStoredResult {
  success: boolean
  documentation_id: string
  results: Array<{
    source_id: string
    object_key: string
    documentation_id: string
    status: string
  }>
  errors: string[]
  processing_time_ms: number
  total_documented: number
  total_failed: number
}

export interface BatchDocumentResponse {
  job_id: string
  status: string
  total_sources: number
}

export interface BatchJobStatus {
  id: string
  // INC-173: 'interrupted' lo calcula el backend al leer el estado cuando el job dice
  // estar vivo pero no late (el worker murio y la fila quedo 'running'). No es un
  // estado guardado en BD: es la verdad observable para no fingir progreso.
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'interrupted'
  total_sources: number
  completed_sources: number
  failed_sources: number
  current_source_key: string | null
  errors: Array<{ source_id?: string; object_key?: string; error: string }>
  progress_pct: number
  created_at: string | null
  updated_at: string | null
  stale_seconds?: number
  detail?: string
}

export const sourceExtractionApi = {
  // Extraction Jobs
  createJob: (connectionId: string, data: {
    object_type: string
    object_keys: string[]
    execution_mode: 'online' | 'background' | 'scheduled'
    scheduled_at?: string
    extraction_options?: Record<string, unknown>
  }) =>
    erpApi.post<ExtractionJob>(
      '/abap-docs/extraction-jobs',
      data,
      { params: { connection_id: connectionId } }
    ),

  listJobs: (connectionId?: string, status?: string, limit?: number, offset?: number) =>
    erpApi.get<{ jobs: ExtractionJob[]; total: number }>(
      '/abap-docs/extraction-jobs',
      { params: { connection_id: connectionId, status, limit, offset } }
    ),

  getJob: (jobId: string) =>
    erpApi.get<ExtractionJob>(`/abap-docs/extraction-jobs/${jobId}`),

  cancelJob: (jobId: string) =>
    erpApi.post(`/abap-docs/extraction-jobs/${jobId}/cancel`),

  // Stored Sources
  listSources: (params?: {
    connection_id?: string
    object_type?: string
    documentation_status?: string
    /** Busqueda parcial (object_key, program_name o descripcion). */
    search?: string
    /** Nombre exacto del objeto (object_key o program_name), case-insensitive. */
    object_name?: string
    limit?: number
    offset?: number
  }) =>
    erpApi.get<{ items: StoredSource[]; total: number }>(
      '/abap-docs/stored-sources',
      { params }
    ),

  getSource: (sourceId: string) =>
    erpApi.get<StoredSourceDetail>(`/abap-docs/stored-sources/${sourceId}`),

  getSourceVersions: (sourceId: string) =>
    erpApi.get<{ versions: SourceVersion[]; total: number }>(
      `/abap-docs/stored-sources/${sourceId}/versions`
    ),

  deleteSource: (sourceId: string) =>
    erpApi.delete(`/abap-docs/stored-sources/${sourceId}`),

  // Generate documentation from stored sources
  generateFromStored: (sourceIds: string[], generateBpmn: boolean = true) =>
    erpApi.post<GenerateFromStoredResult>(
      '/abap-docs/generate-from-stored',
      { source_ids: sourceIds, generate_bpmn: generateBpmn }
    ),

  // Batch documentation (background)
  batchDocument: (data: { source_ids?: string[]; connection_id: string; generate_bpmn?: boolean }) =>
    erpApi.post<BatchDocumentResponse>('/abap-docs/stored-sources/batch-document', data),

  getBatchDocJob: (jobId: string) =>
    erpApi.get<BatchJobStatus>(`/abap-docs/stored-sources/batch-document/${jobId}`),

  cancelBatchDocJob: (jobId: string) =>
    erpApi.post(`/abap-docs/stored-sources/batch-document/${jobId}/cancel`),

  resetDocumentationStatus: (connectionId: string) =>
    erpApi.post('/abap-docs/stored-sources/reset-status', { connection_id: connectionId }),
}

// =============================================================================
// ENRICHMENT API - Correlacion PM-SAL
// =============================================================================

export interface EnrichmentLink {
  id: string
  pm_event_id: string
  case_id: string
  activity: string
  pm_timestamp: string
  pm_user: string
  sal_id: string
  sal_timestamp: string
  sal_user: string
  sal_tcode?: string
  match_type: string
  confidence: number
  delta_seconds: number
  same_user: boolean
}

export interface EnrichmentSummary {
  activity: string
  total_pm_events: number
  matched_events: number
  match_rate: number
  avg_confidence: number
  top_tcodes: Array<{ tcode: string; count: number; description?: string; module?: string }>
  anomaly_count?: number
}

export interface EnrichmentRunResponse {
  status: string
  process_id: string
  pm_events_processed: number
  sal_events_searched: number
  links_created: number
  duration_seconds: number
}

// Navigation Trail types (User Navigation Mining)
export interface NavigationEvent {
  tcode: string
  count: number
  avg_delta_seconds: number
  min_delta_seconds: number
  max_delta_seconds: number
  users: number
  description?: string | null
  module?: string | null
}

export interface NavigationPhase {
  phase: 'BEFORE' | 'DURING' | 'AFTER'
  events: NavigationEvent[]
  total_events: number
}

export interface TcodeSequencePattern {
  sequence: string[]
  count: number
  percentage: number
  sequence_descriptions?: (string | null)[]
  sequence_modules?: (string | null)[]
}

export interface NavigationAnomaly {
  sap_user: string
  expected_pattern: string[]
  actual_pattern: string[]
  deviation_score: number
}

export interface NavigationTrailResponse {
  activity: string
  total_pm_instances: number
  matched_instances: number
  phases: NavigationPhase[]
  patterns: TcodeSequencePattern[]
  anomalies: NavigationAnomaly[]
  available_modules?: string[]
}

// PM Service base URL
const PM_SERVICE_URL = import.meta.env.VITE_PM_SERVICE_URL || `${import.meta.env.VITE_API_URL || '/api'}/v1/pm`

// Create separate axios instance for PM Service
// i18n: el License Optimizer genera el informe Word en el idioma del front
// (Accept-Language lo pone el interceptor único).
const pmApi = applyAuthInterceptors(
  axios.create({
    baseURL: PM_SERVICE_URL,
    headers: { 'Content-Type': 'application/json' },
  })
)

// =============================================================================
// Exportación genérica markdown → Word con marca A.L.D.O (mismo branding que
// Relevamiento). Backend: process-mining /api/v1/doc-export/word. Transversal.
// =============================================================================
export async function exportMarkdownToWord(opts: {
  markdown: string
  title: string
  subtitle?: string
  client?: string
  filename?: string
}): Promise<void> {
  const res = await pmApi.post(
    '/api/v1/doc-export/word',
    {
      markdown: opts.markdown,
      title: opts.title,
      subtitle: opts.subtitle,
      client: opts.client,
      filename: opts.filename,
      language: i18n.language || 'es',
    },
    { responseType: 'blob' },
  )
  const blob = new Blob([res.data as BlobPart], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download =
    (opts.filename || opts.title || 'documento').replace(/[^\w\-. ]+/g, '_').slice(0, 80) + '.docx'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// =============================================================================
// REL-1: Relevamiento de Procesos API (PM Service)
// Backend: process-mining service, prefix /api/v1/relevamiento
// =============================================================================

// Helper to get current user id from zustand persist storage (auth-storage).
// Mirrors getHoldingId(): the auth store persists `user` with `user.id`.
function getUserId(): string | null {
  try {
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      const parsed = JSON.parse(authStorage)
      return parsed?.state?.user?.id || null
    }
  } catch {
    // Ignore parse errors
  }
  return null
}

export interface RelevamientoFilters {
  company_codes: string[]
  plants: string[]
  storage_locations: string[]
  date_from: string | null
  date_to: string | null
  hour_from: number | null
  hour_to: number | null
  weekdays: number[]
}

export interface RelevamientoJobRequest {
  name: string
  client: string
  industry?: string        // dato maestro del tenant (auto-rellenado)
  process_name: string
  family_prefix?: string   // opcional: en modo selección explícita no hay patrón
  job_id_prefix: string
  // Procesos PM (config ids) que cubre el relevamiento — uno o varios E2E. Su
  // discovery real (pm_core) es la fuente del modelo de proceso. Vacío ⇒ el
  // documento se arma solo con el contexto transaccional (SAL).
  extraction_config_ids?: string[]
  tcodes: string[]
  area: string
  sistema_soporte: string
  version: string
  top_n_tcodes: number
  filters: RelevamientoFilters
  language?: string        // idioma de salida del documento (locale i18n del usuario)
}

// Proceso PM con discovery (para el selector multi-proceso del wizard).
export interface RelevamientoPmProcess {
  config_id: string
  name: string
  process_type: string
  total_cases: number
  total_events: number
  processed_at: string | null
}

// Rango de fechas con datos reales en Process Mining (para sugerir en el wizard).
export interface RelevamientoDataRange {
  available: boolean
  date_from: string | null        // rango absoluto disponible (para el banner)
  date_to: string | null
  suggested_from: string | null   // sugerencia = últimos 12 meses (pre-rellenado)
  suggested_to: string | null
  days: number
  total_events: number
}

export type RelevamientoJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'

export interface RelevamientoJobSummary {
  id: string
  name: string
  status: RelevamientoJobStatus
  created_at: string
  completed_at: string | null
  error_message: string | null
}

export interface RelevamientoJobDetail {
  id: string
  holding_id: string
  name: string
  status: RelevamientoJobStatus
  request: RelevamientoJobRequest
  result: Record<string, unknown> | null
  progress: number | null
  error_message: string | null
  created_at: string
  updated_at: string | null
  started_at: string | null
  completed_at: string | null
}

export interface RelevamientoJobCreateResponse {
  job_id: string
  status: RelevamientoJobStatus
  message: string
}

export const relevamientoApi = {
  // Create a relevamiento job. POST additionally needs X-User-ID header.
  createJob: (data: RelevamientoJobRequest) => {
    const userId = getUserId()
    return pmApi.post<RelevamientoJobCreateResponse>(
      '/api/v1/relevamiento/jobs',
      data,
      { headers: userId ? { 'X-User-ID': userId } : undefined }
    )
  },

  // List jobs (most recent first)
  listJobs: (limit = 50) =>
    pmApi.get<RelevamientoJobSummary[]>('/api/v1/relevamiento/jobs', {
      params: { limit },
    }),

  // Get full job detail
  getJob: (jobId: string) =>
    pmApi.get<RelevamientoJobDetail>(`/api/v1/relevamiento/jobs/${jobId}`),

  // Download generated document as a blob
  downloadJob: (jobId: string, kind: 'docx' | 'pdf' | 'markdown') =>
    pmApi.get(`/api/v1/relevamiento/jobs/${jobId}/download`, {
      params: { kind },
      responseType: 'blob',
    }),

  // Delete a job
  deleteJob: (jobId: string) =>
    pmApi.delete(`/api/v1/relevamiento/jobs/${jobId}`),

  // --- Wizard support: org units with real activity (PM service, X-Holding-ID) ---
  // Returns only org units that have actual activity AND mappable users.
  getOrgUnits: () =>
    pmApi.get<{
      companies: Array<{ code: string; name: string }>
      plants: Array<{ code: string; name: string }>
      storage_locations: Array<{ code: string; name: string }>
    }>('/api/v1/relevamiento/org-units'),

  // --- Wizard support: rango de fechas con datos reales en PM ---
  // El usuario no sabe desde cuándo hay datos; esto pre-sugiere el rango.
  getDataRange: () =>
    pmApi.get<RelevamientoDataRange>('/api/v1/relevamiento/data-range'),

  // --- Wizard support: procesos PM con discovery (selector multi-proceso) ---
  // El relevamiento usa el motor PM real como fuente del proceso; el usuario
  // elige uno o varios procesos E2E de esta lista.
  getPmProcesses: () =>
    pmApi.get<{ processes: RelevamientoPmProcess[] }>(
      '/api/v1/relevamiento/pm-processes'
    ),
}

// =============================================================================
// License Optimizer API (PM Service, prefix /api/v1/license)
// Govern → Optimización de Licencias. IP-protected: no SAP table/FM names here.
// =============================================================================

export type LicenseModel = 'perpetual' | 'fue' | 'hybrid'
export type LicenseScenario = 'CONSERVATIVE' | 'MODERATE' | 'AGGRESSIVE'

export interface LicenseRun {
  id: string
  holding_id: string
  rfc_connection_id: string
  license_model: LicenseModel
  window_months: number
  fue_floor_contract: number | null
  status: string
  phase: string | null
  progress_pct: number | null
  error_message: string | null
  gate_report: Record<string, unknown> | null
  sal_coverage: Record<string, unknown> | null
  users_total: number | null
  users_human: number | null
  users_technical: number | null
  users_dormant: number | null
  fue_current: number | null
  fue_target: number | null
  saving_gross_annual: number | null
  saving_net_annual: number | null
  currency: string | null
  label: string | null
  started_at: string | null
  finished_at: string | null
}

export interface LicenseUserProfile {
  id: string
  sap_user: string
  user_kind: string
  current_lic_type: string | null
  last_logon: string | null
  active_days_365: number | null
  distinct_tcodes_365: number | null
  active_months_12: number | null
  close_period_ratio: number | null
  display_approval_ratio: number | null
  taxonomy: string | null
  observed_floor: string | null
  auth_floor: string | null
  target_floor: string | null
  flags: string[] | null
  lever: string | null
  target_lic_type: string | null
  saving_net_annual: number | null
  prerequisite: string | null
}

export interface LicenseScenarioAggregate {
  scenario_tier: LicenseScenario
  saving_gross_annual: number
  saving_net_annual: number
  fue_current: number
  fue_target: number
  users_touched: number
}

export interface LicensePriceRow {
  id?: string
  license_type: string
  pricing_model: 'perpetual' | 'subscription' | 'per_document'
  list_price: number
  maintenance_pct: number
  discount_pct: number
  fue_weight: number | null
  source: string | null
  epistemic: 'CONFIRMED' | 'HIPOTESIS' | 'ASSUMED'
}

export interface LicenseException {
  id: string
  sap_user: string
  reason: string
  approved_by: string | null
  notes: string | null
  created_at: string | null
}

export interface LicenseArtifact {
  id: string
  artifact_type:
    | 'XLSX'
    | 'REPORT_EXEC'
    | 'REPORT_TECH'
    | 'REPORT_COMMERCIAL'
    | 'REPORT_DOCX'
    | 'REPORT_PDF'
    | 'REPORT_MD'
  size_bytes: number | null
  sha256: string | null
  created_at: string | null
  download_url: string | null
}

export interface LicenseSalCoverage {
  available: boolean
  rows: number | null
  months_available: number
  distinct_users: number | null
  distinct_tcodes: number | null
  date_from: string | null
  date_to: string | null
  counts_skipped?: boolean
  has_au1?: boolean | null
  has_au3?: boolean | null
  mode_hint?: 'full_usage' | 'logon_only' | null
}

export interface LicenseAnalytics {
  kpis: {
    fue_current: number
    fue_target: number
    saving_net_annual: number
    saving_gross_annual: number
    currency: string
    users_total: number
    users_human: number
    users_technical: number
    users_dormant: number
    mode: string | null
    ruleset: string | null
  }
  tier_distribution: Array<{ tier: string; users: number }>
  taxonomy: Array<{ taxonomy: string; users: number }>
  auth_vs_observed: Array<{ tier: string; exercised: number; not_exercised: number }>
  fue_bridge: { current: number; dormant: number; downgrade: number; target: number }
  fue_saved: number
  by_type: Array<{ tier: string; rule_description: string | null; count: number; fue: number; why_objects: Array<[string, number]> }>
  savings_by_lever: Array<{ lever: string; label: string; users: number; fue_saved: number }>
  indirect_count: number
  scenarios: Array<{ scenario_tier: string; gross: number; net: number; touched: number }>
}

export const licenseApi = {
  salCoverage: (withCounts = false) =>
    pmApi.get<LicenseSalCoverage>('/api/v1/license/sources/sal', {
      params: withCounts ? { with_counts: true } : undefined,
    }),
  getAnalytics: (runId: string) =>
    pmApi.get<LicenseAnalytics>(`/api/v1/license/runs/${runId}/analytics`),
  generateReport: (runId: string) =>
    pmApi.post(`/api/v1/license/runs/${runId}/report`),
  createRun: (data: {
    rfc_connection_id?: string
    license_model: LicenseModel
    window_months: number
    fue_floor_contract: number
    label?: string
  }) => {
    const userId = getUserId()
    return pmApi.post<LicenseRun>('/api/v1/license/runs', data, {
      headers: userId ? { 'X-User-ID': userId } : undefined,
    })
  },
  listRuns: (limit = 25) =>
    pmApi.get<LicenseRun[]>('/api/v1/license/runs', { params: { limit } }),
  getRun: (runId: string) => pmApi.get<LicenseRun>(`/api/v1/license/runs/${runId}`),
  listUsers: (
    runId: string,
    params?: {
      scenario?: LicenseScenario
      taxonomy?: string
      lever?: string
      license_type?: string
      offset?: number
      limit?: number
    },
  ) => pmApi.get<LicenseUserProfile[]>(`/api/v1/license/runs/${runId}/users`, { params }),
  getScenarios: (runId: string) =>
    pmApi.get<LicenseScenarioAggregate[]>(`/api/v1/license/runs/${runId}/scenarios`),
  listExceptions: () => pmApi.get<LicenseException[]>('/api/v1/license/exceptions'),
  createException: (data: {
    sap_user: string
    reason: string
    approved_by?: string
    notes?: string
    run_id?: string
  }) => pmApi.post<LicenseException>('/api/v1/license/exceptions', data),
  deleteException: (id: string) => pmApi.delete(`/api/v1/license/exceptions/${id}`),
  getPriceBook: () => pmApi.get<LicensePriceRow[]>('/api/v1/license/price-book'),
  upsertPrice: (row: LicensePriceRow) =>
    pmApi.put<LicensePriceRow>('/api/v1/license/price-book', row),
  generateArtifacts: (runId: string, scenario: LicenseScenario = 'MODERATE') =>
    pmApi.post<LicenseArtifact[]>(`/api/v1/license/runs/${runId}/artifacts`, null, {
      params: { scenario },
    }),
  listArtifacts: (runId: string) =>
    pmApi.get<LicenseArtifact[]>(`/api/v1/license/runs/${runId}/artifacts`),
  downloadArtifact: (runId: string, artifactId: string) =>
    pmApi.get(`/api/v1/license/runs/${runId}/artifacts/${artifactId}/download`, {
      responseType: 'blob',
    }),
}

export const enrichmentApi = {
  // Run enrichment correlation
  run: (data: {
    extraction_config_id: string
    connection_id?: string
    date_from?: string
    date_to?: string
    confidence_threshold?: number
    match_window_seconds?: number
  }) => pmApi.post<EnrichmentRunResponse>('/api/v1/pm/enrichment/run', data),

  // Get enrichment links
  getLinks: (params?: {
    process_id?: string
    case_id?: string
    activity?: string
    min_confidence?: number
    limit?: number
  }) => pmApi.get<{ links: EnrichmentLink[]; total_count: number }>('/api/v1/pm/enrichment/links', { params }),

  // Get enrichment summary
  getSummary: (extraction_config_id: string, params?: {
    process_id?: string
    min_confidence?: number
    include_anomaly_counts?: boolean
  }) => pmApi.get<{ summaries: EnrichmentSummary[]; total_activities: number; overall_match_rate: number }>(
    '/api/v1/pm/enrichment/summary',
    { params: { extraction_config_id, ...params } }
  ),

  // Get navigation trail for an activity (User Navigation Mining)
  getNavigationTrail: (params: {
    extraction_config_id: string
    activity: string
    window_seconds?: number
    during_threshold?: number
    limit?: number
  }, signal?: AbortSignal) => pmApi.get<NavigationTrailResponse>(
    '/api/v1/pm/enrichment/navigation-trail',
    { params, signal }
  ),
}

// =============================================================================
// SOD CRUD API - Reglas SoD persistidas
// =============================================================================

export interface SoDRuleDB {
  id: string
  holding_id: string
  name: string
  description?: string
  process_type: string
  tcodes_group_a: string[]
  tcodes_group_b: string[]
  activities_group_a: string[]
  activities_group_b: string[]
  conflict_window_days: number
  min_activities: number
  risk_level: string
  regulatory_reference?: string
  is_active: boolean
  is_system_rule: boolean
  created_at: string
  updated_at: string
}

export interface SoDViolationDB {
  id: string
  rule_id: string
  rule_name: string
  sap_user: string
  action_a_timestamp: string
  action_a_tcode?: string
  action_a_activity?: string
  action_b_timestamp: string
  action_b_tcode?: string
  action_b_activity?: string
  delta_hours?: number
  case_id?: string
  risk_level?: string
  is_acknowledged: boolean
  acknowledged_by?: string
  acknowledged_at?: string
  notes?: string
  created_at: string
}

export interface SoDAnalyzeResponse {
  status: string
  rules_applied: number
  cases_analyzed: number
  violations_found: number
  violations_stored: number
  duration_seconds: number
}

export const sodCrudApi = {
  // List SoD rules
  listRules: (params?: { process_type?: string; is_active?: boolean }) =>
    pmApi.get<{ rules: SoDRuleDB[]; total_count: number }>('/api/v1/pm/sod/rules', { params }),

  // Create SoD rule
  createRule: (data: {
    name: string
    description?: string
    process_type?: string
    tcodes_group_a?: string[]
    tcodes_group_b?: string[]
    activities_group_a?: string[]
    activities_group_b?: string[]
    conflict_window_days?: number
    min_activities?: number
    risk_level?: string
    regulatory_reference?: string
  }) => pmApi.post<SoDRuleDB>('/api/v1/pm/sod/rules', data),

  // Update SoD rule
  updateRule: (id: string, data: Partial<{
    name: string
    description: string
    tcodes_group_a: string[]
    tcodes_group_b: string[]
    activities_group_a: string[]
    activities_group_b: string[]
    conflict_window_days: number
    min_activities: number
    risk_level: string
    regulatory_reference: string
    is_active: boolean
  }>) => pmApi.put<SoDRuleDB>(`/api/v1/pm/sod/rules/${id}`, data),

  // Delete SoD rule
  deleteRule: (id: string) => pmApi.delete(`/api/v1/pm/sod/rules/${id}`),

  // List violations
  listViolations: (params?: {
    rule_id?: string
    sap_user?: string
    case_id?: string
    acknowledged?: boolean
    risk_level?: string
    limit?: number
  }) => pmApi.get<{ violations: SoDViolationDB[]; total_count: number; unacknowledged_count: number }>(
    '/api/v1/pm/sod/violations',
    { params }
  ),

  // Run SoD analysis
  analyze: (data: { extraction_config_id: string; rule_ids?: string[] }) =>
    pmApi.post<SoDAnalyzeResponse>('/api/v1/pm/sod/analyze', data),

  // Acknowledge violation
  acknowledgeViolation: (id: string, notes?: string) =>
    pmApi.put(`/api/v1/pm/sod/violations/${id}/acknowledge`, { notes }),
}

// =============================================================================
// MI CONEXION API - User Profile & RBAC
// =============================================================================

export interface IdentityInfo {
  email: string
  first_name?: string
  last_name?: string
  full_name?: string
  auth_provider: string
  external_id?: string
  email_verified: boolean
  mfa_enabled: boolean
}

export interface HoldingContext {
  id: string
  code: string
  name: string
}

export interface CurrentContext {
  holding?: HoldingContext
  empresa?: { id: string; code: string; name: string }
  area?: { id: string; code: string; name: string }
  available_holdings: HoldingContext[]
}

export interface RoleInfo {
  id: string
  code: string
  name: string
  scope_type: string
  scope_name?: string
}

export interface EffectiveRolesInfo {
  roles: RoleInfo[]
  permissions: string[]
  role_level: number
  role_level_name: string
}

export interface UserProfileData {
  id: string
  user_id: string
  language: string
  timezone: string
  date_format: string
  time_format: string
  theme: string
  ui_density: string
  sidebar_collapsed: boolean
  notifications_email: boolean
  notifications_in_app: boolean
  notifications_digest: string
  avatar_url?: string
  avatar_type: string
  custom_settings: Record<string, unknown>
  created_at: string
  updated_at?: string
}

export interface MyConnectionResponse {
  user_id: string
  identity: IdentityInfo
  context: CurrentContext
  authorization: EffectiveRolesInfo
  profile: UserProfileData
  features: string[]
  last_login?: string
  created_at: string
}

export interface UserProfileUpdate {
  language?: string
  timezone?: string
  date_format?: string
  time_format?: string
  theme?: string
  ui_density?: string
  sidebar_collapsed?: boolean
  notifications_email?: boolean
  notifications_in_app?: boolean
  notifications_digest?: string
  custom_settings?: Record<string, unknown>
}

export interface PasswordChangeRequest {
  current_password: string
  new_password: string
}

export interface EffectivePermissions {
  user_id: string
  permissions: string[]
  roles: Array<{ id: string; code: string; name: string; scope_type: string }>
  scope: {
    holding_id?: string
    empresa_id?: string
    area_id?: string
  }
}

export const myConnectionApi = {
  // Get complete Mi Conexión data
  getMyConnection: () =>
    api.get<MyConnectionResponse>('/v1/me'),

  // Update profile preferences
  updateProfile: (data: UserProfileUpdate) =>
    api.patch<UserProfileData>('/v1/me', data),

  // Upload avatar
  uploadAvatar: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post<{ avatar_url: string; avatar_type: string }>('/v1/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  // Delete avatar
  deleteAvatar: () =>
    api.delete<{ success: boolean }>('/v1/me/avatar'),

  // Change password
  changePassword: (data: PasswordChangeRequest) =>
    api.post<{ success: boolean }>('/v1/me/change-password', data),

  // Get effective permissions
  getPermissions: (params?: { holding_id?: string; empresa_id?: string; area_id?: string }) =>
    api.get<EffectivePermissions>('/v1/me/permissions', { params }),
}

// =============================================================================
// RBAC ADMIN API - Roles, Permissions, Features
// =============================================================================

export interface Permission {
  id: string
  code: string
  name: string
  description?: string
  module: string
  status: string
  created_at: string
}

// RBAC_36 (#371): lo que manda el servidor (RoleResponse en schemas/rbac.py)
export interface RolePermission {
  permission_id: string
  code: string
  name: string
  module: string
}

export interface Role {
  id: string
  code: string
  name: string
  description?: string | null
  scope_type: string
  priority: number
  holding_id?: string | null
  // RBAC_33 (#365): el nivel que da el rol (1 GAM Admin ... 7 Usuario Basico); null = no da nivel
  role_level?: number | null
  is_system: boolean
  status: string
  created_at: string
  permissions: RolePermission[]
}

export interface RoleBinding {
  id: string
  user_id: string
  user_email?: string
  user_name?: string
  role_id: string
  role_code?: string
  role_name?: string
  scope_type: string
  channel_partner_id?: string
  holding_id?: string
  empresa_id?: string
  area_id?: string
  is_active: boolean
  granted_by?: string
  granted_at: string
  expires_at?: string
  created_at: string
}

export interface FeatureFlag {
  id: string
  code: string
  name: string
  description?: string
  holding_id?: string
  enabled: boolean
  config: Record<string, unknown>
  enabled_at?: string
  disabled_at?: string
  status: string
  created_at: string
  updated_at?: string
}

export interface SecurityAuditLog {
  id: string
  event_type: string
  actor_id?: string
  actor_email?: string
  target_type?: string
  target_id?: string
  holding_id?: string
  ip_address?: string
  user_agent?: string
  details: Record<string, unknown>
  success: boolean
  error_message?: string
  created_at: string
}

export interface CreateRoleDTO {
  code: string
  name: string
  description?: string
  scope_type: string
  holding_id: string
  // RBAC_33 (#365): obligatorio; no se cambia despues
  role_level: number
  permission_codes: string[]
}

// El codigo, el holding, el alcance y el nivel no se cambian
export interface UpdateRoleDTO {
  name?: string
  description?: string
  permission_codes?: string[]
}

export interface CreateRoleBindingDTO {
  user_id: string
  role_id: string
  scope_type: string
  channel_partner_id?: string
  holding_id?: string
  empresa_id?: string
  area_id?: string
  expires_at?: string
}

export interface CreateFeatureFlagDTO {
  code: string
  name: string
  description?: string
  holding_id?: string
  enabled?: boolean
  config?: Record<string, unknown>
}

export interface UpdateFeatureFlagDTO {
  name?: string
  description?: string
  enabled?: boolean
  config?: Record<string, unknown>
}

// F5_21c (#615): constancias de eliminación; solo el GAM Admin (la API responde 403 al resto)
export const retentionCertificatesApi = {
  listClients: () =>
    api.get<RetentionClientsResponse>('/v1/admin/retention/clients'),
  // Fechas YYYY-MM-DD (UTC), las dos obligatorias
  getCertificate: (params: { holding_id: string; date_from: string; date_to: string }) =>
    api.get<RetentionCertificate>('/v1/admin/retention/certificate', { params }),
  // F5_22c (#710): lo que un servicio guardó sin cliente («system»)
  getSystemCertificate: (params: { date_from: string; date_to: string }) =>
    api.get<RetentionCertificate>('/v1/admin/retention/certificate/system', { params }),
}

export const rbacApi = {
  // === PERMISSIONS ===
  listPermissions: (params?: { module?: string; search?: string; skip?: number; limit?: number }) =>
    api.get<{ items: Permission[]; total: number }>('/v1/admin/rbac/permissions', { params }),

  getPermission: (id: string) =>
    api.get<Permission>(`/v1/admin/rbac/permissions/${id}`),

  // === ROLES ===
  listRoles: (params?: { scope_type?: string; search?: string; skip?: number; limit?: number }) =>
    api.get<{ items: Role[]; total: number }>('/v1/admin/rbac/roles', { params }),

  getRole: (id: string) =>
    api.get<Role>(`/v1/admin/rbac/roles/${id}`),

  createRole: (data: CreateRoleDTO) =>
    api.post<Role>('/v1/admin/rbac/roles', data),

  updateRole: (id: string, data: UpdateRoleDTO) =>
    api.patch<Role>(`/v1/admin/rbac/roles/${id}`, data),

  // Los permisos de un rol se cambian con updateRole (permission_codes)
  deleteRole: (id: string) =>
    api.delete(`/v1/admin/rbac/roles/${id}`),

  // === ROLE BINDINGS ===
  listRoleBindings: (params?: { user_id?: string; role_id?: string; is_active?: boolean; skip?: number; limit?: number }) =>
    api.get<{ items: RoleBinding[]; total: number }>('/v1/admin/rbac/role-bindings', { params }),

  createRoleBinding: (data: CreateRoleBindingDTO) =>
    api.post<RoleBinding>('/v1/admin/rbac/role-bindings', data),

  updateRoleBinding: (id: string, data: { is_active?: boolean; expires_at?: string }) =>
    api.patch<RoleBinding>(`/v1/admin/rbac/role-bindings/${id}`, data),

  deleteRoleBinding: (id: string) =>
    api.delete(`/v1/admin/rbac/role-bindings/${id}`),

  bulkCreateRoleBindings: (bindings: CreateRoleBindingDTO[]) =>
    api.post<{ success_count: number; failed_count: number; bindings: RoleBinding[] }>('/v1/admin/rbac/role-bindings/bulk', { bindings }),

  // === FEATURE FLAGS ===
  listFeatureFlags: (params?: { search?: string; enabled?: boolean; skip?: number; limit?: number }) =>
    api.get<{ items: FeatureFlag[]; total: number }>('/v1/admin/rbac/features', { params }),

  createFeatureFlag: (data: CreateFeatureFlagDTO) =>
    api.post<FeatureFlag>('/v1/admin/rbac/features', data),

  updateFeatureFlag: (id: string, data: UpdateFeatureFlagDTO) =>
    api.patch<FeatureFlag>(`/v1/admin/rbac/features/${id}`, data),

  // === AUDIT LOG ===
  listAuditLog: (params?: {
    event_type?: string
    actor_id?: string
    target_type?: string
    from_date?: string
    to_date?: string
    success?: boolean
    skip?: number
    limit?: number
  }) => api.get<{ items: SecurityAuditLog[]; total: number }>('/v1/admin/rbac/audit-log', { params }),

  getAuditEventTypes: () =>
    api.get<{ event_types: string[] }>('/v1/admin/rbac/audit-log/event-types'),

  // === PERMISSION CHECK ===
  checkPermission: (data: { permission_code: string; holding_id?: string; empresa_id?: string; area_id?: string }) =>
    api.post<{ allowed: boolean; permission_code: string }>('/v1/admin/rbac/check-permission', data),

  // === SEED DATA ===
  seedRBACData: (clearExisting?: boolean) =>
    api.post('/v1/admin/rbac/seed', null, { params: { clear_existing: clearExisting } }),
}

// ============== OData Endpoints ==============
// BAJA-002: OData Generator Service

export interface ODataEndpoint {
  id: string
  holding_id: string
  endpoint_code: string
  endpoint_name: string | null
  description: string | null
  source_type: 'event_log' | 'discovery' | 'custom_query'
  process_type: string | null
  extraction_config_id: string | null
  auth_type: 'sso' | 'basic' | 'api_key' | 'none'
  filterable_fields: string[] | null
  sortable_fields: string[] | null
  selectable_fields: string[] | null
  field_mappings: Record<string, string> | null
  max_page_size: number
  default_page_size: number
  rate_limit_per_minute: number
  cache_ttl_seconds: number
  is_active: boolean
  created_at: string
  updated_at: string
  odata_url: string
}

export interface ODataEndpointAccess {
  id: string
  endpoint_id: string
  access_type: 'user' | 'api_key'
  user_email: string | null
  api_key_name: string | null
  api_key_prefix: string | null
  is_active: boolean
  expires_at: string | null
  created_at: string
  last_used_at: string | null
}

export interface ODataAccessLog {
  id: string
  timestamp: string
  user: string | null
  auth_type: string | null
  status_code: number
  rows_returned: number
  response_time_ms: number
  client_ip: string | null
}

export interface GeneratedApiKey {
  id: string
  api_key: string // Solo se muestra UNA VEZ
  api_key_name: string
  api_key_prefix: string
  expires_at: string | null
  created_at: string
}

export interface ODataEndpointCreate {
  endpoint_code: string
  endpoint_name?: string
  description?: string
  source_type: 'event_log' | 'discovery' | 'custom_query'
  process_type?: string
  extraction_config_id?: string
  auth_type?: 'sso' | 'basic' | 'api_key' | 'none'
  filterable_fields?: string[]
  sortable_fields?: string[]
  field_mappings?: Record<string, string>
  max_page_size?: number
  default_page_size?: number
  rate_limit_per_minute?: number
  cache_ttl_seconds?: number
  is_active?: boolean
}

export const odataEndpointsApi = {
  // CRUD
  list: (params?: { is_active?: boolean; skip?: number; limit?: number }) =>
    api.get<{ endpoints: ODataEndpoint[]; total: number }>('/v1/odata-endpoints', { params }),

  get: (id: string) =>
    api.get<ODataEndpoint>(`/v1/odata-endpoints/${id}`),

  create: (data: ODataEndpointCreate) =>
    api.post<ODataEndpoint>('/v1/odata-endpoints', data),

  update: (id: string, data: Partial<ODataEndpointCreate>) =>
    api.patch<ODataEndpoint>(`/v1/odata-endpoints/${id}`, data),

  delete: (id: string) =>
    api.delete(`/v1/odata-endpoints/${id}`),

  preview: (id: string, limit?: number) =>
    api.get<{ endpoint_code: string; row_count: number; preview: Record<string, unknown>[] }>(
      `/v1/odata-endpoints/${id}/preview`,
      { params: { limit } }
    ),

  // Access (API Keys / Users)
  listAccess: (endpointId: string) =>
    api.get<ODataEndpointAccess[]>(`/v1/odata-endpoints/${endpointId}/access`),

  generateKey: (endpointId: string, name: string, expiresDays?: number) =>
    api.post<GeneratedApiKey>(
      `/v1/odata-endpoints/${endpointId}/generate-key`,
      null,
      { params: { api_key_name: name, expires_days: expiresDays } }
    ),

  revokeAccess: (endpointId: string, accessId: string) =>
    api.delete(`/v1/odata-endpoints/${endpointId}/access/${accessId}`),

  // Logs
  getLogs: (endpointId: string, params?: { skip?: number; limit?: number; status_code?: number }) =>
    api.get<{ endpoint_id: string; logs: ODataAccessLog[] }>(
      `/v1/odata-endpoints/${endpointId}/logs`,
      { params }
    ),
}

// AI Metrics API
export const aiMetricsApi = {
  getSummary: (params?: { date_from?: string; date_to?: string }) =>
    api.get('/v1/ai-metrics/summary', { params }),
  getDaily: (params?: { date_from?: string; date_to?: string }) =>
    api.get('/v1/ai-metrics/daily', { params }),
  getConversations: (params?: { page?: number; page_size?: number; feature?: string; user_id?: string }) =>
    api.get('/v1/ai-metrics/conversations', { params }),
  getRequests: (params?: { conversation_id?: string; page?: number; page_size?: number }) =>
    api.get('/v1/ai-metrics/requests', { params }),
}

// =============================================================================
// Audit Analytics API (PM service)
// =============================================================================

export const auditAnalyticsApi = {
  // Step 1: Jobs
  createJobFromDocuments: (data: {
    title: string
    description?: string
    date_from: string
    date_to: string
    documentation_ids: string[]
  }) => pmApi.post('/api/v1/audit-analytics/jobs/from-documents', data),

  listJobs: (params?: { limit?: number; offset?: number }) =>
    pmApi.get('/api/v1/audit-analytics/jobs', { params }),

  getJob: (jobId: string) =>
    pmApi.get(`/api/v1/audit-analytics/jobs/${jobId}`),

  // Step 2: Signatures
  runStep2: (jobId: string) =>
    pmApi.post(`/api/v1/audit-analytics/jobs/${jobId}/steps/2/run`),

  listSignatures: (jobId: string) =>
    pmApi.get(`/api/v1/audit-analytics/jobs/${jobId}/signatures`),

  editSignature: (jobId: string, sigId: string, data: Record<string, unknown>) =>
    pmApi.patch(`/api/v1/audit-analytics/jobs/${jobId}/signatures/${sigId}`, data),

  approveSignature: (jobId: string, sigId: string) =>
    pmApi.post(`/api/v1/audit-analytics/jobs/${jobId}/signatures/${sigId}/approve`),

  discardSignature: (jobId: string, sigId: string) =>
    pmApi.post(`/api/v1/audit-analytics/jobs/${jobId}/signatures/${sigId}/discard`),

  summarizeSignature: (jobId: string, sigId: string) =>
    pmApi.post(`/api/v1/audit-analytics/jobs/${jobId}/signatures/${sigId}/summarize`),

  // Step 3: SAL Pipeline
  precheckStep3: (jobId: string) =>
    pmApi.post(`/api/v1/audit-analytics/jobs/${jobId}/steps/3/precheck`),

  runStep3: (jobId: string) =>
    pmApi.post(`/api/v1/audit-analytics/jobs/${jobId}/steps/3/run`),

  listEvents: (jobId: string, params?: { user?: string; case?: string; limit?: number; offset?: number }) =>
    pmApi.get(`/api/v1/audit-analytics/jobs/${jobId}/events`, { params }),

  listTransitions: (jobId: string) =>
    pmApi.get(`/api/v1/audit-analytics/jobs/${jobId}/transitions`),

  // Step 4: Mining
  runStep4: (jobId: string) =>
    pmApi.post(`/api/v1/audit-analytics/jobs/${jobId}/steps/4/run`),

  getReport: (jobId: string) =>
    pmApi.get(`/api/v1/audit-analytics/jobs/${jobId}/report`),

  exportXes: (jobId: string) =>
    pmApi.get(`/api/v1/audit-analytics/jobs/${jobId}/export/xes`, { responseType: 'blob' }),
}

// ============================================================================
// ALTA-030: Process Recommendations API
// Backend: backend/api_gateway/src/app/routers/recommendations.py
// ============================================================================
import type {
  ProcessRecommendation,
  ProcessRecommendationList,
  RecommendationStatus,
  GenerateRecommendationsRequest,
  GenerateRecommendationsResponse,
} from '@/components/recommendations/types'

export const recommendationsApi = {
  list: (params?: {
    analysis_id?: string
    process_id?: string
    status?: RecommendationStatus
    priority?: string
    page?: number
    page_size?: number
  }) =>
    api.get<ProcessRecommendationList>('/v1/pm/recommendations', { params }),

  get: (id: string) =>
    api.get<ProcessRecommendation>(`/v1/pm/recommendations/${id}`),

  updateStatus: (id: string, status: RecommendationStatus) =>
    api.patch<ProcessRecommendation>(`/v1/pm/recommendations/${id}/status`, { status }),

  update: (id: string, body: Partial<ProcessRecommendation>) =>
    api.put<ProcessRecommendation>(`/v1/pm/recommendations/${id}`, body),

  remove: (id: string) =>
    api.delete(`/v1/pm/recommendations/${id}`),

  generate: (body: GenerateRecommendationsRequest) =>
    api.post<GenerateRecommendationsResponse>('/v1/pm/recommendations/generate', body),
}

// =============================================================================
// BADM (comercialmente "Monarca") — Bupry Advanced Data Migration
// Endpoints servidos por backend/badm/ vía api_gateway en /v1/badm/*.
// =============================================================================
export interface BadmProjectCreate {
  name: string
  description?: string
  route?: 'B1_TO_S4' | 'ECC_TO_S4'
  modality?: 'ONESHOT' | 'COEXIST' | 'PERPETUAL'
  country?: string
  sap_connection_id?: string
  scope_objects?: string[]
}

export interface BadmProjectUpdate {
  name?: string
  description?: string
  route?: 'B1_TO_S4' | 'ECC_TO_S4'
  modality?: 'ONESHOT' | 'COEXIST' | 'PERPETUAL'
  status?: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'ARCHIVED'
  country?: string
  sap_connection_id?: string
  scope_objects?: string[]
}

export interface BadmAvailableBusinessObject {
  code: string
  name: string
  description: string
  domain: string
  functional_area: string
  s4hana_concept: string
  tables: string[]
  dependencies: string[]
}

export const badmApi = {
  listProjects: () => api.get('/v1/badm/projects'),
  getProject: (id: string) => api.get(`/v1/badm/projects/${id}`),
  createProject: (body: BadmProjectCreate) => api.post('/v1/badm/projects', body),
  updateProject: (id: string, body: BadmProjectUpdate) =>
    api.patch(`/v1/badm/projects/${id}`, body),
  deleteProject: (id: string) => api.delete(`/v1/badm/projects/${id}`),
  listBusinessObjects: () => api.get<BadmAvailableBusinessObject[]>('/v1/badm/scope-objects'),

  // Business Objects (F3.1 - Pivote BO-centric)
  seedBusinessObjects: (projectId: string) =>
    api.post<{ created: number; skipped: number; total: number }>(
      `/v1/badm/${projectId}/business-objects/seed`
    ),
  listProjectBusinessObjects: (projectId: string) =>
    api.get<BadmBusinessObject[]>(`/v1/badm/${projectId}/business-objects`),
  getBusinessObjectSummary: (boId: string) =>
    api.get<BadmBusinessObjectSummary>(`/v1/badm/business-objects/${boId}/summary`),
  // P1.4 audit Baco 2026-05-12: desglose por tabla SAP del BO.
  getBusinessObjectTables: (boId: string) =>
    api.get<Array<{ table: string; records: number; last_extraction: string | null }>>(
      `/v1/badm/business-objects/${boId}/tables`,
    ),
  updateBusinessObject: (boId: string, data: Partial<{
    owner_id?: string
    status?: BadmBusinessObject['status']
    priority?: number
  }>) =>
    api.patch<{ id: string; updated: string[] }>(`/v1/badm/business-objects/${boId}`, data),
}

export interface BadmCatalogEntry {
  id: string
  holding_id: string
  project_id: string
  connection_id: string | null
  source_system: string
  table_name: string
  business_name: string | null
  technical_name: string | null
  domain: string | null
  row_count_estimate: number | null
  last_introspected_at: string | null
  in_scope: boolean
  status: string
  created_at: string
  updated_at: string
}

export interface BadmCatalogCreate {
  project_id: string
  connection_id?: string | null
  source_system?: string
  table_name: string
  business_name?: string
  technical_name?: string
  domain?: string
  in_scope?: boolean
}

export interface BadmCatalogUpdate {
  business_name?: string
  technical_name?: string
  domain?: string
  in_scope?: boolean
  status?: string
}

export interface BadmDiscoverRequest {
  project_id: string
  connection_id: string
  search?: string
  limit?: number
}

export interface BadmDiscoverResponse {
  project_id: string
  connection_id: string
  discovered_count: number
  inserted_count: number
  skipped_existing: number
  sample: string[]
}

export interface BadmMasterTable {
  table_name: string
  domain: string
  business_name: string
  recommended: boolean
  notes: string
}

export interface BadmSeedDefaultsRequest {
  project_id: string
  domains?: string[]
  include_optional?: boolean
}

export interface BadmSeedDefaultsResponse {
  project_id: string
  inserted_count: number
  skipped_existing: number
  domains_seeded: string[]
}

// =============================================================================
// Staging / Workflow / Clusters / Audit / Enrichment (F2.7–F2.10)
// =============================================================================
// Business Objects (F3.1 - Pivote BO-centric)
export interface BadmBusinessObject {
  id: string
  code: string
  name: string
  description?: string
  domain: string
  tables: string[]
  status: 'DISCOVERY' | 'DATA_EXTRACTION' | 'QUALITY' | 'DEDUP' | 'MAPPING' | 'VALIDATION' | 'CUTOVER' | 'COMPLETED'
  owner_id?: string
  priority: number
  records_count: number
  quality_score: number
  duplicates_count: number
  mappings_complete: boolean
  created_at: string
  updated_at: string
}

export interface BadmBusinessObjectSummary extends BadmBusinessObject {
  kpis: {
    records_count: number
    quality_score: number
    duplicates_count: number
    mappings_complete: boolean
  }
  stepper_state: {
    data_extraction: boolean
    quality: boolean
    dedup: boolean
    mapping: boolean
    validation: boolean
    cutover: boolean
  }
  suggested_actions: string[]
}

export interface BadmStagingRecord {
  id: string
  holding_id: string
  project_id: string
  entity_type: string
  source_system: string
  source_pk: string
  payload: Record<string, unknown>
  batch_id: string | null
  extracted_at: string
  status: string
  source_metadata?: Record<string, unknown>
  connection_name?: string
  record_count?: number
}

export interface BadmSourceSystemSummary {
  source_system: string
  source_label: string
  record_count: number
  last_extraction?: string
  entity_types: string[]
}

export interface BadmStagingMultiSourceResponse {
  records: BadmStagingRecord[]
  source_summary: BadmSourceSystemSummary[]
  total_records: number
  total_sources: number
}

export interface BadmStagingBulkRecord {
  entity_type: string
  source_system?: string
  source_pk: string
  payload: Record<string, unknown>
}

export interface BadmStagingBulkRequest {
  project_id: string
  batch_id?: string
  records: BadmStagingBulkRecord[]
}

export interface BadmStagingBulkResponse {
  project_id: string
  inserted: number
  skipped_duplicates: number
  batch_id: string
}

export interface BadmWorkflowTask {
  id: string
  holding_id: string
  project_id: string
  task_type: string
  title: string
  description: string | null
  assignee_id: string | null
  status: 'NEW' | 'IN_REVIEW' | 'APPROVED' | 'REJECTED' | 'CLOSED'
  priority: 'BLOCKER' | 'HIGH' | 'MEDIUM' | 'LOW'
  sla_due_at: string | null
  payload: Record<string, unknown> | null
  resolution: string | null
  created_at: string
  updated_at: string
}

export interface BadmWorkflowTaskCreate {
  project_id: string
  task_type: string
  title: string
  description?: string
  assignee_id?: string
  priority?: BadmWorkflowTask['priority']
  sla_due_at?: string
  payload?: Record<string, unknown>
}

export interface BadmWorkflowTaskUpdate {
  title?: string
  description?: string
  assignee_id?: string
  status?: BadmWorkflowTask['status']
  priority?: BadmWorkflowTask['priority']
  sla_due_at?: string
  payload?: Record<string, unknown>
  resolution?: string
}

export interface BadmDuplicateCluster {
  id: string
  holding_id: string
  project_id: string
  rule_id: string | null
  entity_type: string
  score: number
  status: 'PENDING' | 'CONFIRMED' | 'REJECTED' | 'MERGED'
  master_record_id: string | null
  member_ids: string[]
  notes: string | null
  created_at: string
  updated_at: string
}

export interface BadmAuditLogEntry {
  id: string
  holding_id: string
  project_id: string | null
  actor_id: string | null
  actor_email: string | null
  action: string
  entity_type: string
  entity_id: string | null
  timestamp: string
}

export interface BadmEnrichmentCall {
  id: string
  project_id: string
  provider: string
  staging_record_id: string | null
  status_code: number | null
  duration_ms: number | null
  cost_units: number
  called_at: string
}

export interface BadmEnrichmentLookupRequest {
  project_id: string
  provider: 'SII' | 'AEAT' | 'GLEIF' | 'OFAC' | 'GTIN' | 'GEO'
  query: Record<string, unknown>
  staging_record_id?: string
}

export interface BadmEnrichmentLookupResponse {
  id: string
  provider: string
  status_code: number | null
  duration_ms: number | null
  response: Record<string, unknown>
}

export interface BadmEvaluateQualityRequest {
  project_id: string
  rule_ids?: string[]
  entity_type?: string
}

export interface BadmEvaluateQualityResponse {
  project_id: string
  rules_evaluated: number
  records_scanned: number
  pass_count: number
  fail_count: number
  warn_count: number
  skipped_count: number
  by_severity: Record<string, number>
  by_rule: Array<Record<string, unknown>>
}

export interface BadmDetectDuplicatesRequest {
  project_id: string
  rule_id?: string
  entity_type?: string
}

export interface BadmDetectDuplicatesResponse {
  project_id: string
  rules_evaluated: number
  records_scanned: number
  clusters_created: number
  pairs_compared: number
  by_rule: Array<Record<string, unknown>>
}

export const badmStagingApi = {
  list: (projectId: string, entityType?: string, limit = 100) =>
    api.get<BadmStagingRecord[]>('/v1/badm/staging', {
      params: { project_id: projectId, entity_type: entityType, limit },
    }),
  listMultiSource: (
    projectId: string,
    entityType?: string,
    sourceSystem?: string,
    limit = 100,
    offset = 0,
    extra?: {
      table?: string
      country?: string
      role?: 'customer' | 'vendor' | 'general' | undefined
    },
  ) =>
    api.get<BadmStagingMultiSourceResponse>('/v1/badm/staging/multi-source', {
      params: {
        project_id: projectId,
        entity_type: entityType,
        source_system: sourceSystem,
        limit,
        offset,
        table: extra?.table,
        country: extra?.country,
        role: extra?.role,
      },
    }),
  getSourceSystems: () =>
    api.get<Array<{ code: string; label: string }>>('/v1/badm/staging/source-systems'),
  bulk: (body: BadmStagingBulkRequest) =>
    api.post<BadmStagingBulkResponse>('/v1/badm/staging/bulk', body),
  remove: (id: string) => api.delete(`/v1/badm/staging/${id}`),
  /**
   * Extract REAL SAP table rows via RFC into staging_records.
   * Backend: POST /v1/badm/extract (orquesta erp_connector → .NET agent → RFC_READ_TABLE).
   */
  extract: (body: {
    project_id: string
    connection_id: string
    table_name: string
    fields?: string[]
    where?: string
    max_rows?: number
    /** ROWSKIPS — saltar N filas. Útil para paginar cliente-side. */
    offset?: number
    /** Código del Business Object — solo para enriquecer la bitácora. */
    bo_code?: string
  }) =>
    api.post<{
      project_id: string
      table: string
      fetched: number
      inserted: number
      skipped_duplicates: number
      batch_id: string | null
      offset: number
      next_offset: number
      has_more: boolean
    }>('/v1/badm/extract', body),

  /**
   * Valida una lista de tablas contra DD02L del SAP destino.
   * Útil para descartar tablas inexistentes antes de extraer.
   */
  validateTables: (body: { connection_id: string; tables: string[] }) =>
    api.post<{
      results: Record<string, { exists: boolean | null; tabclass?: string; contflag?: string; error?: string }>
      exists: string[]
      missing: string[]
      unverified: string[]
    }>('/v1/badm/validate-tables', body),

  /**
   * Bitácora de extracciones — visible para admin/soporte.
   * Cada llamada al extractor genera una fila aquí.
   */
  listExtractionLogs: (params: {
    project_id?: string
    table_name?: string
    status_filter?: string
    error_key?: string
    since_minutes?: number
    limit?: number
  }) =>
    api.get<Array<{
      id: string
      project_id: string
      connection_id: string
      bo_code: string | null
      table_name: string
      fields_used: string[] | null
      chunk_size: number | null
      offset_used: number | null
      fetched: number | null
      inserted: number | null
      skipped_duplicates: number | null
      has_more: boolean | null
      duration_ms: number | null
      status: 'OK' | 'ERROR' | 'RETRY'
      error_key: string | null
      error_message: string | null
      created_at: string | null
    }>>('/v1/badm/extraction-logs', { params }),

  extractionLogsSummary: (params: { project_id?: string; since_minutes?: number }) =>
    api.get<{
      window_minutes: number
      total_calls: number
      ok: number
      error: number
      rows_fetched: number
      by_error_key: Record<string, number>
      by_table_with_errors: Record<string, number>
    }>('/v1/badm/extraction-logs/summary', { params }),
}

export const badmWorkflowApi = {
  list: (projectId: string, assigneeId?: string, statusFilter?: string) =>
    api.get<BadmWorkflowTask[]>('/v1/badm/workflow/tasks', {
      params: { project_id: projectId, assignee_id: assigneeId, status_filter: statusFilter },
    }),
  create: (body: BadmWorkflowTaskCreate) =>
    api.post<BadmWorkflowTask>('/v1/badm/workflow/tasks', body),
  update: (id: string, body: BadmWorkflowTaskUpdate) =>
    api.patch<BadmWorkflowTask>(`/v1/badm/workflow/tasks/${id}`, body),
  remove: (id: string) => api.delete(`/v1/badm/workflow/tasks/${id}`),
}

export const badmClustersApi = {
  list: (projectId: string, entityType?: string, statusFilter?: string) =>
    api.get<BadmDuplicateCluster[]>('/v1/badm/dedup/clusters', {
      params: { project_id: projectId, entity_type: entityType, status_filter: statusFilter },
    }),
  update: (id: string, body: Partial<{ status: string; master_record_id: string; notes: string }>) =>
    api.patch<BadmDuplicateCluster>(`/v1/badm/dedup/clusters/${id}`, body),
  remove: (id: string) => api.delete(`/v1/badm/dedup/clusters/${id}`),
  detect: (body: BadmDetectDuplicatesRequest) =>
    api.post<BadmDetectDuplicatesResponse>('/v1/badm/dedup/detect', body),
}

export const badmAuditApi = {
  list: (projectId?: string, entityType?: string, limit = 100) =>
    api.get<BadmAuditLogEntry[]>('/v1/badm/audit/log', {
      params: { project_id: projectId, entity_type: entityType, limit },
    }),
}

export const badmEnrichmentApi = {
  lookup: (body: BadmEnrichmentLookupRequest) =>
    api.post<BadmEnrichmentLookupResponse>('/v1/badm/enrichment/lookup', body),
  listCalls: (projectId: string, provider?: string) =>
    api.get<BadmEnrichmentCall[]>('/v1/badm/enrichment/calls', {
      params: { project_id: projectId, provider },
    }),
  removeCall: (id: string) => api.delete(`/v1/badm/enrichment/calls/${id}`),
}

export const badmQualityRuntimeApi = {
  evaluate: (body: BadmEvaluateQualityRequest) =>
    api.post<BadmEvaluateQualityResponse>('/v1/badm/quality/evaluate', body),
  listResults: (projectId: string, ruleId?: string, statusFilter?: string) =>
    api.get('/v1/badm/quality/results', {
      params: { project_id: projectId, rule_id: ruleId, status_filter: statusFilter },
    }),
}

export type BadmRunType = 'dry_run' | 'mock' | 'cutover' | 'perpetual_window'
export type BadmRunStatus = 'running' | 'completed' | 'failed' | 'cancelled'

export interface BadmPipelineRun {
  id: string
  holding_id: string
  project_id: string
  run_type: BadmRunType
  status: BadmRunStatus
  started_at: string
  completed_at: string | null
  duration_ms: number | null
  summary: Record<string, unknown> | null
  retention_days: number
}

export interface BadmPipelineEvent {
  ts: string
  type: string
  payload: Record<string, unknown>
}

export interface BadmPipelineRunWithEvents extends BadmPipelineRun {
  events: BadmPipelineEvent[]
}

export interface BadmPipelineRunCreate {
  project_id: string
  run_type?: BadmRunType
}

export interface BadmPipelineDemoRequest {
  project_id: string
  nodes?: number
}

export interface BadmPipelineDemoResponse {
  run_id: string
  events_recorded: number
}

export const badmPipelineApi = {
  list: (projectId: string) =>
    api.get<BadmPipelineRun[]>('/v1/badm/pipeline/runs', { params: { project_id: projectId } }),
  get: (runId: string) =>
    api.get<BadmPipelineRunWithEvents>(`/v1/badm/pipeline/runs/${runId}`),
  create: (body: BadmPipelineRunCreate) =>
    api.post<BadmPipelineRun>('/v1/badm/pipeline/runs', body),
  remove: (runId: string) => api.delete(`/v1/badm/pipeline/runs/${runId}`),
  demoRun: (body: BadmPipelineDemoRequest) =>
    api.post<BadmPipelineDemoResponse>('/v1/badm/pipeline/demo-run', body),
}

export interface BadmCockpitObject {
  id: string
  name: string
  domain: string
  release: string
  approach: string
  source_table_hint: string
  notes: string
}

export interface BadmCockpitExportRequest {
  project_id: string
  cockpit_object_id: string
  mapping_id?: string
  rows?: Record<string, unknown>[]
}

export interface BadmCockpitExportResponse {
  project_id: string
  cockpit_object_id: string
  rows_included: number
  xml: string
  filename: string
}

export const badmCockpitApi = {
  listObjects: () => api.get<BadmCockpitObject[]>('/v1/badm/cockpit/objects'),
  exportXml: (body: BadmCockpitExportRequest) =>
    api.post<BadmCockpitExportResponse>('/v1/badm/cockpit/export', body),
}

export interface BadmDedupRule {
  id: string
  holding_id: string
  project_id: string
  code: string
  name: string
  entity_type: string
  description: string | null
  blocking: Record<string, unknown> | null
  features: Record<string, number> | null
  thresholds: Record<string, number> | null
  survivorship: Record<string, string> | null
  template_origin: string | null
  active: boolean
  created_at: string
  updated_at: string
}

export interface BadmStandardDedupRule {
  code: string
  name: string
  entity_type: string
  description: string
  blocking: Record<string, unknown>
  features: Record<string, number>
  thresholds: Record<string, number>
  survivorship: Record<string, string>
}

export interface BadmDedupRuleCreate {
  project_id: string
  code: string
  name: string
  entity_type: string
  description?: string
  blocking?: Record<string, unknown>
  features?: Record<string, number>
  thresholds?: Record<string, number>
  survivorship?: Record<string, string>
  active?: boolean
}

export interface BadmDedupRuleUpdate {
  name?: string
  entity_type?: string
  description?: string
  blocking?: Record<string, unknown>
  features?: Record<string, number>
  thresholds?: Record<string, number>
  survivorship?: Record<string, string>
  active?: boolean
}

export interface BadmSeedDedupRequest {
  project_id: string
  entity_types?: string[]
}

export interface BadmSeedDedupResponse {
  project_id: string
  inserted_count: number
  skipped_existing: number
  entities_seeded: string[]
}

export const badmDedupApi = {
  list: (projectId: string) =>
    api.get<BadmDedupRule[]>('/v1/badm/dedup/rules', { params: { project_id: projectId } }),
  get: (id: string) => api.get<BadmDedupRule>(`/v1/badm/dedup/rules/${id}`),
  create: (body: BadmDedupRuleCreate) =>
    api.post<BadmDedupRule>('/v1/badm/dedup/rules', body),
  update: (id: string, body: BadmDedupRuleUpdate) =>
    api.patch<BadmDedupRule>(`/v1/badm/dedup/rules/${id}`, body),
  remove: (id: string) => api.delete(`/v1/badm/dedup/rules/${id}`),
  listStandard: () =>
    api.get<BadmStandardDedupRule[]>('/v1/badm/dedup/standard-rules'),
  seedDefaults: (body: BadmSeedDedupRequest) =>
    api.post<BadmSeedDedupResponse>('/v1/badm/dedup/seed-defaults', body),
}

export type BadmRuleType =
  | 'SYNTACTIC'
  | 'SEMANTIC'
  | 'REFERENTIAL'
  | 'BUSINESS'
  | 'CROSS_SYSTEM'
export type BadmSeverity = 'BLOCKER' | 'ERROR' | 'WARN' | 'INFO'

export interface BadmQualityRule {
  id: string
  holding_id: string
  project_id: string
  code: string
  name: string
  rule_type: BadmRuleType
  severity: BadmSeverity
  target_entity: string | null
  description: string | null
  definition: Record<string, unknown> | null
  template_origin: string | null
  active: boolean
  created_at: string
  updated_at: string
}

export interface BadmStandardRule {
  code: string
  name: string
  rule_type: BadmRuleType
  severity: BadmSeverity
  target_entity: string
  description: string
  definition: Record<string, unknown>
}

export interface BadmQualityRuleCreate {
  project_id: string
  code: string
  name: string
  rule_type: BadmRuleType
  severity?: BadmSeverity
  target_entity?: string
  description?: string
  definition?: Record<string, unknown>
  active?: boolean
}

export interface BadmQualityRuleUpdate {
  name?: string
  rule_type?: BadmRuleType
  severity?: BadmSeverity
  target_entity?: string
  description?: string
  definition?: Record<string, unknown>
  active?: boolean
}

export interface BadmSeedQualityRequest {
  project_id: string
  rule_types?: BadmRuleType[]
  severities?: BadmSeverity[]
}

export interface BadmSeedQualityResponse {
  project_id: string
  inserted_count: number
  skipped_existing: number
  types_seeded: string[]
}

export const badmQualityApi = {
  list: (projectId: string) =>
    api.get<BadmQualityRule[]>('/v1/badm/quality/rules', { params: { project_id: projectId } }),
  get: (id: string) => api.get<BadmQualityRule>(`/v1/badm/quality/rules/${id}`),
  create: (body: BadmQualityRuleCreate) =>
    api.post<BadmQualityRule>('/v1/badm/quality/rules', body),
  update: (id: string, body: BadmQualityRuleUpdate) =>
    api.patch<BadmQualityRule>(`/v1/badm/quality/rules/${id}`, body),
  remove: (id: string) => api.delete(`/v1/badm/quality/rules/${id}`),
  listStandard: () => api.get<BadmStandardRule[]>('/v1/badm/quality/standard-rules'),
  seedDefaults: (body: BadmSeedQualityRequest) =>
    api.post<BadmSeedQualityResponse>('/v1/badm/quality/seed-defaults', body),
}

export interface BadmMappingTemplateSummary {
  id: string
  name: string
  description: string | null
  source_table: string
  target_object: string
  field_count: number
  version: number
}

export interface BadmMappingTemplateField {
  source_field: string
  target_field: string
  transformation: string | null
  mandatory: boolean
  notes: string | null
}

export interface BadmMappingTemplateDetail extends BadmMappingTemplateSummary {
  fields: BadmMappingTemplateField[]
}

export interface BadmMappingFieldEntry {
  id: string
  mapping_id: string
  source_field: string
  target_field: string
  transformation: string | null
  mandatory: boolean
  notes: string | null
  ordinal: number
  created_at: string
}

export interface BadmMapping {
  id: string
  holding_id: string
  project_id: string
  name: string
  description: string | null
  source_table: string
  target_object: string
  template_origin: string | null
  version: number
  status: 'DRAFT' | 'REVIEW' | 'APPROVED' | 'ARCHIVED'
  created_at: string
  updated_at: string
}

export interface BadmMappingWithFields extends BadmMapping {
  fields: BadmMappingFieldEntry[]
}

export interface BadmMappingCreate {
  project_id: string
  name: string
  description?: string
  source_table: string
  target_object: string
}

export interface BadmMappingUpdate {
  name?: string
  description?: string
  source_table?: string
  target_object?: string
  status?: 'DRAFT' | 'REVIEW' | 'APPROVED' | 'ARCHIVED'
}

export interface BadmApplyTemplateRequest {
  project_id: string
  template_id: string
  name_override?: string
}

export const badmMappingsApi = {
  list: (projectId: string) =>
    api.get<BadmMapping[]>('/v1/badm/mappings', { params: { project_id: projectId } }),
  get: (id: string) => api.get<BadmMappingWithFields>(`/v1/badm/mappings/${id}`),
  create: (body: BadmMappingCreate) => api.post<BadmMapping>('/v1/badm/mappings', body),
  update: (id: string, body: BadmMappingUpdate) =>
    api.patch<BadmMapping>(`/v1/badm/mappings/${id}`, body),
  remove: (id: string) => api.delete(`/v1/badm/mappings/${id}`),
  listTemplates: () =>
    api.get<BadmMappingTemplateSummary[]>('/v1/badm/mappings/templates'),
  getTemplate: (templateId: string) =>
    api.get<BadmMappingTemplateDetail>(`/v1/badm/mappings/templates/${templateId}`),
  applyTemplate: (body: BadmApplyTemplateRequest) =>
    api.post<BadmMappingWithFields>('/v1/badm/mappings/from-template', body),
}

export const badmCatalogApi = {
  list: (projectId: string) =>
    api.get<BadmCatalogEntry[]>('/v1/badm/catalog', { params: { project_id: projectId } }),
  get: (id: string) => api.get(`/v1/badm/catalog/${id}`),
  create: (body: BadmCatalogCreate) => api.post<BadmCatalogEntry>('/v1/badm/catalog', body),
  update: (id: string, body: BadmCatalogUpdate) =>
    api.patch<BadmCatalogEntry>(`/v1/badm/catalog/${id}`, body),
  remove: (id: string) => api.delete(`/v1/badm/catalog/${id}`),
  discover: (body: BadmDiscoverRequest) =>
    api.post<BadmDiscoverResponse>('/v1/badm/catalog/discover', body),
  listMasterTables: () =>
    api.get<BadmMasterTable[]>('/v1/badm/catalog/master-tables'),
  seedDefaults: (body: BadmSeedDefaultsRequest) =>
    api.post<BadmSeedDefaultsResponse>('/v1/badm/catalog/seed-defaults', body),
}

// Export erpApi to fix build errors
export { erpApi }

// =============================================================================
// ALDO Readiness API (microservicio aldo-readiness — port 8010)
// Backend: backend/aldo_readiness/src/app/routers/{runs,clean_core,documents}.py
// INFRA_03 (#234): siempre por el gateway. En Azure el servicio tiene ingress
// interno; no hay URL directa que el navegador pueda (ni deba) usar.
// =============================================================================

const READINESS_URL = `${API_BASE_URL}/v1/readiness`

const readinessAxios = applyAuthInterceptors(
  axios.create({
    baseURL: READINESS_URL,
    headers: { 'Content-Type': 'application/json' },
  })
)

/**
 * Baja un archivo con la sesión del usuario (Bearer + X-Holding-ID del cliente) y lo
 * guarda en el navegador. Para endpoints detrás del gateway, donde un <a href> no
 * lleva el token. Usa el nombre que mande Content-Disposition, o `fallbackName`.
 */
export async function downloadBlob(client: AxiosInstance, url: string, fallbackName: string, params?: Record<string, string>) {
  const res = await client.get(url, { responseType: 'blob', params })
  const cd = (res.headers['content-disposition'] as string | undefined) || ''
  const m = /filename="?([^"]+)"?/.exec(cd)
  const filename = m?.[1] || fallbackName
  const objectUrl = URL.createObjectURL(res.data as Blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(objectUrl)
}

export type ReadinessDepth = 'executive' | 'technical' | 'commercial'

export interface ReadinessRun {
  id: string
  holding_id: string
  connection_id: string | null
  status: string
  overall_score: number | null
  label: string | null
  started_at: string | null
  completed_at: string | null
}

export interface ReadinessDocument {
  depth: ReadinessDepth
  depth_label: string
  status: 'queued' | 'running' | 'success' | 'partial' | 'failed'
  docx_url: string | null
  pdf_url: string | null
  page_count: number | null
  sha256_docx: string | null
  sha256_pdf: string | null
  generated_at: string | null
  error_message: string | null
}

export interface CleanCoreSummary {
  run_id: string
  td_categories: Array<{
    category: string
    object_count: number
    severity: string
    weight: number
    normalized_score: number
    monetary_estimate_usd: number | null
    sample_objects: string[]
  }>
  cc_dimensions: Array<{ dimension: string; score: number; weight: number }>
  strategy_counts: Record<string, number>
  strategy_total: number
}

export const readinessApi = {
  // Runs
  createRun: (data: { connection_id?: string; overall_score?: number; label?: string }) =>
    readinessAxios.post<ReadinessRun>('/runs', data),
  getRun: (runId: string) =>
    readinessAxios.get<ReadinessRun>(`/runs/${runId}`),

  // Clean Core extraction
  extractCleanCore: (
    runId: string,
    params?: { connection_id?: string; hourly_rate_usd?: number }
  ) =>
    readinessAxios.post(`/runs/${runId}/extract-clean-core`, null, { params }),
  getCleanCoreSummary: (runId: string) =>
    readinessAxios.get<CleanCoreSummary>(`/runs/${runId}/clean-core/summary`),

  // Documents
  generateDocument: (runId: string, depth: ReadinessDepth) =>
    readinessAxios.post(`/runs/${runId}/generate-document`, { depth }),
  listDocuments: (runId: string) =>
    readinessAxios.get<ReadinessDocument[]>(`/runs/${runId}/documents`),
  // El servicio responde 302 a su descarga firmada; el gateway la sigue por dentro y
  // entrega el archivo. Va con la sesión: un <a href> no llevaría el token.
  downloadDocument: (runId: string, depth: ReadinessDepth, kind: 'pdf' | 'docx' = 'pdf') =>
    downloadBlob(
      readinessAxios,
      `/runs/${runId}/documents/${depth}`,
      `aldo-readiness-${depth}-${runId.slice(0, 8)}.${kind}`,
      { kind }
    ),
}

// Build trigger: 1765452163
// Build trigger: 1765452167
// Build trigger: 1767178000 - Force rebuild for PMAnalysis fix
